import React, { useState, useEffect } from 'react';
import { X, ArrowLeft, Trash2 } from 'lucide-react';
import api from '../services/api';
import { toast } from 'react-toastify';
import '../styles/UnitDealDetailsSlider.css';
import { generateColorSelectionAmendment } from '../utils/pdfGenerator';
import DocumentGallery from './DocumentGallery';
import ConfirmDialog from './ConfirmDialog';
import { getImageUrlCached } from '../services/imageService';

interface UnitDealDetailsSliderProps {
  workspaceId: string;
  unitId: string;
  onClose: () => void;
  onSuccess?: () => void;
}

interface UnitDeal {
  _id: string;
  dealId: string | null;
  project: string;
  projectId: string;
  modelCollection: string;
  modelName: string;
  type: string;
  unitNumber: string;
  unitStatus: string;
  deal: string;
  dealStatus: string;
  designStudioStatus: string;
  colorCollection: string;
  packages: string;
  options: string;
  accessToPP: boolean;
  designStudio: boolean;
  colorSelection: boolean;
  packageSelection: boolean;
  parkingSelection: boolean;
  bicycleSelection: boolean;
  lockerSelection: boolean;
  layoutCustomization: boolean;
}

interface PhaseSelection {
  _id: string;
  selectionType: 'color' | 'package' | 'option';
  itemId: string;
  itemName: string;
  cost: number;
  price: number;
  quantity?: number;
  isUpgrade: boolean;
  phase: string;
  location?: string;
  categoryName?: string;
  description?: string;
  constructionNotes?: string;
}

interface ColorCollection {
  _id: string;
  colorCollectionId: string;
  colorCollectionName: string;
  description?: string;
  constructionNotes?: string;
  isUpgrade: boolean;
  cost: number;
  price: number;
}

interface Package {
  _id: string;
  packageId: string;
  packageName: string;
  description?: string;
  constructionNotes?: string;
  location?: string;
  isUpgrade: boolean;
  cost: number;
  price: number;
  incompatiblePackages?: Array<{ _id: string; name?: string }>;
  incompatiblePersonalizationOptions?: Array<{ _id: string; name?: string }>;
  parentPackage?: { id: string; name?: string } | null;
  includedOptions?: Array<{ _id: string; name?: string }>;
}

interface Option {
  _id: string;
  optionId: string;
  optionName: string;
  description?: string;
  constructionNotes?: string;
  location?: string;
  categoryId?: string;
  categoryName?: string;
  isUpgrade: boolean;
  cost: number;
  price: number;
  incompatibleOptions?: Array<{ _id: string; name?: string }>;
  incompatiblePackages?: Array<{ _id: string; name?: string }>;
  onlySoldWithPackages?: Array<{ _id: string; name?: string }>;
  onlySoldWithOptions?: Array<{ _id: string; name?: string }>;
}

const UnitDealDetailsSlider: React.FC<UnitDealDetailsSliderProps> = ({
  workspaceId,
  unitId,
  onClose,
  onSuccess
}) => {
  // Helper function to strip HTML tags
  const stripHtmlTags = (html: string | undefined): string => {
    if (!html) return '';
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  };

  const [unitDeal, setUnitDeal] = useState<UnitDeal | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('available-selections');
  const [projectOngoingPhase, setProjectOngoingPhase] = useState<string>('One');

  // Available Selections state
  const [colorCollections, setColorCollections] = useState<ColorCollection[]>([]);
  const [selectedColorCollectionId, setSelectedColorCollectionId] = useState<string | null>(null);
  const [loadingColorCollections, setLoadingColorCollections] = useState(false);

  // Packages state
  const [packages, setPackages] = useState<Package[]>([]);
  const [selectedPackageIds, setSelectedPackageIds] = useState<string[]>([]);
  const [loadingPackages, setLoadingPackages] = useState(false);

  // Options state
  const [options, setOptions] = useState<Option[]>([]);
  const [selectedOptionIds, setSelectedOptionIds] = useState<string[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  // Loading states for individual actions
  const [addingColorCollection, setAddingColorCollection] = useState<string | null>(null);
  const [addingPackage, setAddingPackage] = useState<string | null>(null);
  const [removingPackage, setRemovingPackage] = useState<string | null>(null);
  const [addingOption, setAddingOption] = useState<string | null>(null);
  const [removingOption, setRemovingOption] = useState<string | null>(null);

  // Confirmation dialog state
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingColorCollectionId, setPendingColorCollectionId] = useState<string | null>(null);

  // Info modal state
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [infoModalData, setInfoModalData] = useState<{
    title: string;
    description?: string;
    constructionNotes?: string;
  } | null>(null);

  // Design Studio state
  const [activePhaseTab, setActivePhaseTab] = useState<string>('One');
  const [phaseSelections, setPhaseSelections] = useState<PhaseSelection[]>([]);
  const [loadingPhaseSelections, setLoadingPhaseSelections] = useState(false);
  const [deletingSelection, setDeletingSelection] = useState<string | null>(null);
  const [phaseStatuses, setPhaseStatuses] = useState<{[key: string]: string}>({
    One: 'NOT STARTED',
    Two: 'NOT STARTED',
    Three: 'NOT STARTED',
    Four: 'NOT STARTED',
    Five: 'NOT STARTED'
  });
  const [showStatusConfirmDialog, setShowStatusConfirmDialog] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState<{phase: string, status: string} | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  // Template generation state
  const [showDocumentTypeModal, setShowDocumentTypeModal] = useState(false);
  const [showTemplateSelectModal, setShowTemplateSelectModal] = useState(false);
  const [selectedGenerationType, setSelectedGenerationType] = useState<string>('');
  const [availableTemplates, setAvailableTemplates] = useState<any[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [generatingDocument, setGeneratingDocument] = useState(false);

  // Parking Selection state
  const [parkingAllocated, setParkingAllocated] = useState<Array<{type: string; allocated: number}>>([]);
  const [parkingSelected, setParkingSelected] = useState<Array<{type: string; selected: number}>>([]);
  const [selectedParkingSpaces, setSelectedParkingSpaces] = useState<any[]>([]);
  const [loadingParkingAllocation, setLoadingParkingAllocation] = useState(false);
  const [parkingSelectionStatus, setParkingSelectionStatus] = useState<string>('NOT STARTED');
  const [showParkingStatusConfirmDialog, setShowParkingStatusConfirmDialog] = useState(false);
  const [pendingParkingStatusChange, setPendingParkingStatusChange] = useState<string | null>(null);
  const [updatingParkingStatus, setUpdatingParkingStatus] = useState(false);

  // Bicycle Parking Selection state
  const [bicycleAllocated, setBicycleAllocated] = useState<Array<{type: string; allocated: number}>>([]);
  const [bicycleSelected, setBicycleSelected] = useState<Array<{type: string; selected: number}>>([]);
  const [selectedBicycleSpaces, setSelectedBicycleSpaces] = useState<any[]>([]);
  const [loadingBicycleAllocation, setLoadingBicycleAllocation] = useState(false);
  const [bicycleSelectionStatus, setBicycleSelectionStatus] = useState<string>('NOT STARTED');
  const [showBicycleStatusConfirmDialog, setShowBicycleStatusConfirmDialog] = useState(false);
  const [pendingBicycleStatusChange, setPendingBicycleStatusChange] = useState<string | null>(null);
  const [updatingBicycleStatus, setUpdatingBicycleStatus] = useState(false);

  // Locker Selection state
  const [lockerAllocated, setLockerAllocated] = useState<Array<{type: string; allocated: number}>>([]);
  const [lockerSelected, setLockerSelected] = useState<Array<{type: string; selected: number}>>([]);
  const [selectedLockerSpaces, setSelectedLockerSpaces] = useState<any[]>([]);
  const [loadingLockerAllocation, setLoadingLockerAllocation] = useState(false);
  const [lockerSelectionStatus, setLockerSelectionStatus] = useState<string>('NOT STARTED');
  const [showLockerStatusConfirmDialog, setShowLockerStatusConfirmDialog] = useState(false);
  const [pendingLockerStatusChange, setPendingLockerStatusChange] = useState<string | null>(null);
  const [updatingLockerStatus, setUpdatingLockerStatus] = useState(false);

  // Delete confirmation state
  const [showDeleteParkingConfirm, setShowDeleteParkingConfirm] = useState(false);
  const [parkingToDelete, setParkingToDelete] = useState<any>(null);
  const [isDeletingParking, setIsDeletingParking] = useState(false);
  const [showDeleteBicycleConfirm, setShowDeleteBicycleConfirm] = useState(false);
  const [bicycleToDelete, setBicycleToDelete] = useState<any>(null);
  const [isDeletingBicycle, setIsDeletingBicycle] = useState(false);
  const [showDeleteLockerConfirm, setShowDeleteLockerConfirm] = useState(false);
  const [lockerToDelete, setLockerToDelete] = useState<any>(null);
  const [isDeletingLocker, setIsDeletingLocker] = useState(false);

  // Summary state
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [summaryData, setSummaryData] = useState<any>(null);
  const [expandedPhases, setExpandedPhases] = useState<{[key: string]: boolean}>({
    One: true,
    Two: true,
    Three: true,
    Four: true,
    Five: true
  });
  const [heroImageUrl, setHeroImageUrl] = useState<string | null>(null);
  const [loadingHeroImage, setLoadingHeroImage] = useState(false);

  useEffect(() => {
    // Disable body scroll when slider is open
    document.body.style.overflow = 'hidden';

    return () => {
      // Re-enable body scroll when slider closes
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    loadUnitDealDetails();
  }, [workspaceId, unitId]);

  useEffect(() => {
    if (activeTab === 'available-selections' && !loadingColorCollections && colorCollections.length === 0) {
      loadColorCollections();
    }
    if (activeTab === 'design-studio' && !loadingPhaseSelections) {
      loadPhaseSelections(activePhaseTab);
    }
    if (activeTab === 'parking-selection' && !loadingParkingAllocation && selectedParkingSpaces.length === 0) {
      loadParkingAllocation();
    }
    if (activeTab === 'bicycle-parking-selection' && !loadingBicycleAllocation && selectedBicycleSpaces.length === 0) {
      loadBicycleAllocation();
    }
    if (activeTab === 'locker-selection' && !loadingLockerAllocation && selectedLockerSpaces.length === 0) {
      loadLockerAllocation();
    }
    if (activeTab === 'summary' && !loadingSummary && !summaryData) {
      loadSummaryData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, activePhaseTab]);

  useEffect(() => {
    if (selectedColorCollectionId) {
      loadPackages();
      loadOptions();
    } else {
      setPackages([]);
      setSelectedPackageIds([]);
      setOptions([]);
      setSelectedOptionIds([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedColorCollectionId]);

  const loadUnitDealDetails = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get(`/unit-deals/${workspaceId}/unit/${unitId}`);
      setUnitDeal(response.data);

      // Load phase statuses from response
      if (response.data.phaseStatuses) {
        setPhaseStatuses(response.data.phaseStatuses);
      }

      // Update projectOngoingPhase from the response if available
      if (response.data.projectOngoingPhase) {
        setProjectOngoingPhase(response.data.projectOngoingPhase);
      }
    } catch (err: any) {
      console.error('Error loading unit deal details:', err);
      setError(err.response?.data?.message || 'Failed to load unit deal details');
      toast.error('Failed to load unit deal details');
    } finally {
      setLoading(false);
    }
  };

  const loadPhaseSelections = async (phase: string) => {
    try {
      setLoadingPhaseSelections(true);
      const response = await api.get(`/unit-deals/${workspaceId}/unit/${unitId}/phase-selections`, {
        params: { phase }
      });
      setPhaseSelections(response.data.selections || []);
    } catch (err: any) {
      console.error('Error loading phase selections:', err);
      toast.error('Failed to load phase selections');
      setPhaseSelections([]);
    } finally {
      setLoadingPhaseSelections(false);
    }
  };

  const loadColorCollections = async () => {
    try {
      setLoadingColorCollections(true);
      const response = await api.get(`/unit-deals/${workspaceId}/unit/${unitId}/color-collections`);
      setColorCollections(response.data.colorCollections || []);
      setSelectedColorCollectionId(response.data.selectedColorCollectionId || null);
      // Update projectOngoingPhase from the response if available
      if (response.data.ongoingSelectionPhase) {
        setProjectOngoingPhase(response.data.ongoingSelectionPhase);
      }
    } catch (err: any) {
      console.error('Error loading color collections:', err);
      toast.error('Failed to load color collections');
    } finally {
      setLoadingColorCollections(false);
    }
  };

  const handleAddColorCollectionClick = (colorCollectionId: string) => {
    // Check if a different color collection is already selected
    if (selectedColorCollectionId && selectedColorCollectionId !== colorCollectionId) {
      // Show confirmation dialog
      setPendingColorCollectionId(colorCollectionId);
      setShowConfirmDialog(true);
    } else {
      // Proceed directly if no selection exists
      handleAddColorCollection(colorCollectionId);
    }
  };

  const handleConfirmChange = () => {
    if (pendingColorCollectionId) {
      handleAddColorCollection(pendingColorCollectionId);
    }
    setShowConfirmDialog(false);
    setPendingColorCollectionId(null);
  };

  const handleCancelChange = () => {
    setShowConfirmDialog(false);
    setPendingColorCollectionId(null);
  };

  const handleAddColorCollection = async (colorCollectionId: string) => {
    try {
      setAddingColorCollection(colorCollectionId);
      const response = await api.post(`/unit-deals/${workspaceId}/unit/${unitId}/color-collections`, {
        colorCollectionId
      });

      toast.success(response.data.message || 'Color collection added successfully');

      // Reload color collections to update UI
      await loadColorCollections();

      // Reload packages and options as they depend on color collection
      await loadPackages();
      await loadOptions();

      // Optionally call onSuccess callback
      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      console.error('Error adding color collection:', err);
      toast.error(err.response?.data?.message || 'Failed to add color collection');
    } finally {
      setAddingColorCollection(null);
    }
  };

  const loadPackages = async () => {
    if (!selectedColorCollectionId) {
      setPackages([]);
      setSelectedPackageIds([]);
      return;
    }

    try {
      setLoadingPackages(true);
      const response = await api.get(`/unit-deals/${workspaceId}/unit/${unitId}/packages`);
      setPackages(response.data.packages || []);
      setSelectedPackageIds(response.data.selectedPackageIds || []);
    } catch (err: any) {
      console.error('Error loading packages:', err);
      toast.error('Failed to load packages');
    } finally {
      setLoadingPackages(false);
    }
  };

  const handleAddPackage = async (packageId: string) => {
    try {
      setAddingPackage(packageId);
      // Add the package (backend will automatically add included options)
      const response = await api.post(`/unit-deals/${workspaceId}/unit/${unitId}/packages`, {
        packageId
      });

      toast.success(response.data.message || 'Package added successfully');

      // Show info about automatically added included options
      if (response.data.addedIncludedOptions && response.data.addedIncludedOptions.length > 0) {
        const optionNames = response.data.addedIncludedOptions.map((opt: any) => opt.name).join(', ');
        toast.info(`Included options added: ${optionNames}`);
      }

      // Reload packages and options to update UI
      await loadPackages();
      await loadOptions();

      // Optionally call onSuccess callback
      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      console.error('Error adding package:', err);
      toast.error(err.response?.data?.message || 'Failed to add package');
    } finally {
      setAddingPackage(null);
    }
  };

  const handleRemovePackage = async (packageId: string) => {
    try {
      setRemovingPackage(packageId);
      const response = await api.delete(`/unit-deals/${workspaceId}/unit/${unitId}/packages`, {
        data: { packageId }
      });

      toast.success(response.data.message || 'Package removed successfully');

      // Show info about automatically removed included options
      if (response.data.removedIncludedOptions && response.data.removedIncludedOptions.length > 0) {
        const optionNames = response.data.removedIncludedOptions.map((opt: any) => opt.name).join(', ');
        toast.info(`Included options removed: ${optionNames}`);
      }

      // Reload packages and options to update UI
      await loadPackages();
      await loadOptions();

      // Optionally call onSuccess callback
      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      console.error('Error removing package:', err);
      toast.error(err.response?.data?.message || 'Failed to remove package');
    } finally {
      setRemovingPackage(null);
    }
  };

  const loadOptions = async () => {
    if (!selectedColorCollectionId) {
      setOptions([]);
      setSelectedOptionIds([]);
      return;
    }

    try {
      setLoadingOptions(true);
      const response = await api.get(`/unit-deals/${workspaceId}/unit/${unitId}/options`);
      setOptions(response.data.options || []);
      setSelectedOptionIds(response.data.selectedOptionIds || []);
    } catch (err: any) {
      console.error('Error loading options:', err);
      toast.error('Failed to load options');
    } finally {
      setLoadingOptions(false);
    }
  };

  const handleAddOption = async (optionId: string) => {
    try {
      setAddingOption(optionId);
      const response = await api.post(`/unit-deals/${workspaceId}/unit/${unitId}/options`, {
        optionId
      });

      toast.success(response.data.message || 'Option added successfully');

      // Reload options to update UI
      await loadOptions();

      // Optionally call onSuccess callback
      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      console.error('Error adding option:', err);
      toast.error(err.response?.data?.message || 'Failed to add option');
    } finally {
      setAddingOption(null);
    }
  };

  const handleRemoveOption = async (optionId: string) => {
    try {
      setRemovingOption(optionId);
      const response = await api.delete(`/unit-deals/${workspaceId}/unit/${unitId}/options`, {
        data: { optionId }
      });

      toast.success(response.data.message || 'Option removed successfully');

      // Reload options to update UI
      await loadOptions();

      // Optionally call onSuccess callback
      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      console.error('Error removing option:', err);
      toast.error(err.response?.data?.message || 'Failed to remove option');
    } finally {
      setRemovingOption(null);
    }
  };

  // Load parking allocation
  const loadParkingAllocation = async () => {
    try {
      setLoadingParkingAllocation(true);
      const response = await api.get(`/unit-deals/${workspaceId}/unit/${unitId}/parking-allocation`);
      setParkingAllocated(response.data.parkingAllocated || []);
      setParkingSelected(response.data.parkingSelected || []);
      setSelectedParkingSpaces(response.data.selectedParkingSpaces || []);
      setParkingSelectionStatus(response.data.parkingSelectionStatus || 'NOT STARTED');
    } catch (err: any) {
      console.error('Error loading parking allocation:', err);
      toast.error('Failed to load parking allocation');
    } finally {
      setLoadingParkingAllocation(false);
    }
  };

  // Handle parking selection status change
  const handleParkingStatusClick = (status: string) => {
    // Don't allow changing if it's already the current status
    if (parkingSelectionStatus === status) {
      return;
    }

    setPendingParkingStatusChange(status);
    setShowParkingStatusConfirmDialog(true);
  };

  const handleConfirmParkingStatusChange = async () => {
    if (!pendingParkingStatusChange) return;

    try {
      setUpdatingParkingStatus(true);

      await api.patch(`/unit-deals/${workspaceId}/unit/${unitId}/parking-status`, {
        status: pendingParkingStatusChange
      });

      // Update local state
      setParkingSelectionStatus(pendingParkingStatusChange);

      toast.success('Parking selection status updated successfully');

      // Close dialog
      setShowParkingStatusConfirmDialog(false);
      setPendingParkingStatusChange(null);
    } catch (err: any) {
      console.error('Error updating parking status:', err);
      toast.error(err.response?.data?.message || 'Failed to update parking status');
    } finally {
      setUpdatingParkingStatus(false);
    }
  };

  const handleCancelParkingStatusChange = () => {
    setShowParkingStatusConfirmDialog(false);
    setPendingParkingStatusChange(null);
  };

  // Load bicycle allocation
  const loadBicycleAllocation = async () => {
    try {
      setLoadingBicycleAllocation(true);
      const response = await api.get(`/unit-deals/${workspaceId}/unit/${unitId}/bicycle-allocation`);
      setBicycleAllocated(response.data.bicycleAllocated || []);
      setBicycleSelected(response.data.bicycleSelected || []);
      setSelectedBicycleSpaces(response.data.selectedBicycleSpaces || []);
      setBicycleSelectionStatus(response.data.bicycleSelectionStatus || 'NOT STARTED');
    } catch (err: any) {
      console.error('Error loading bicycle allocation:', err);
      toast.error('Failed to load bicycle allocation');
    } finally {
      setLoadingBicycleAllocation(false);
    }
  };

  // Handle bicycle selection status change
  const handleBicycleStatusClick = (status: string) => {
    if (bicycleSelectionStatus === status) return;
    setPendingBicycleStatusChange(status);
    setShowBicycleStatusConfirmDialog(true);
  };

  const handleConfirmBicycleStatusChange = async () => {
    if (!pendingBicycleStatusChange) return;
    try {
      setUpdatingBicycleStatus(true);
      await api.patch(`/unit-deals/${workspaceId}/unit/${unitId}/bicycle-status`, {
        status: pendingBicycleStatusChange
      });
      setBicycleSelectionStatus(pendingBicycleStatusChange);
      toast.success('Bicycle selection status updated successfully');
      setShowBicycleStatusConfirmDialog(false);
      setPendingBicycleStatusChange(null);
    } catch (err: any) {
      console.error('Error updating bicycle status:', err);
      toast.error(err.response?.data?.message || 'Failed to update bicycle status');
    } finally {
      setUpdatingBicycleStatus(false);
    }
  };

  const handleCancelBicycleStatusChange = () => {
    setShowBicycleStatusConfirmDialog(false);
    setPendingBicycleStatusChange(null);
  };

  // Load locker allocation
  const loadLockerAllocation = async () => {
    try {
      setLoadingLockerAllocation(true);
      const response = await api.get(`/unit-deals/${workspaceId}/unit/${unitId}/locker-allocation`);
      setLockerAllocated(response.data.lockerAllocated || []);
      setLockerSelected(response.data.lockerSelected || []);
      setSelectedLockerSpaces(response.data.selectedLockerSpaces || []);
      setLockerSelectionStatus(response.data.lockerSelectionStatus || 'NOT STARTED');
    } catch (err: any) {
      console.error('Error loading locker allocation:', err);
      toast.error('Failed to load locker allocation');
    } finally {
      setLoadingLockerAllocation(false);
    }
  };

  // Handle locker selection status change
  const handleLockerStatusClick = (status: string) => {
    if (lockerSelectionStatus === status) return;
    setPendingLockerStatusChange(status);
    setShowLockerStatusConfirmDialog(true);
  };

  const handleConfirmLockerStatusChange = async () => {
    if (!pendingLockerStatusChange) return;
    try {
      setUpdatingLockerStatus(true);
      await api.patch(`/unit-deals/${workspaceId}/unit/${unitId}/locker-status`, {
        status: pendingLockerStatusChange
      });
      setLockerSelectionStatus(pendingLockerStatusChange);
      toast.success('Locker selection status updated successfully');
      setShowLockerStatusConfirmDialog(false);
      setPendingLockerStatusChange(null);
    } catch (err: any) {
      console.error('Error updating locker status:', err);
      toast.error(err.response?.data?.message || 'Failed to update locker status');
    } finally {
      setUpdatingLockerStatus(false);
    }
  };

  const handleCancelLockerStatusChange = () => {
    setShowLockerStatusConfirmDialog(false);
    setPendingLockerStatusChange(null);
  };

  // Delete handlers for parking
  const handleConfirmDeleteParking = async () => {
    if (!parkingToDelete) return;

    try {
      setIsDeletingParking(true);
      await api.post(`/workspaces/${workspaceId}/parking-spaces/release`, {
        spaceId: parkingToDelete.spaceId
      });
      toast.success('Parking space removed successfully');
      setShowDeleteParkingConfirm(false);
      setParkingToDelete(null);
      // Reload parking allocation data
      await loadParkingAllocation();
    } catch (error: any) {
      console.error('Error removing parking space:', error);
      toast.error(error.response?.data?.message || 'Failed to remove parking space');
    } finally {
      setIsDeletingParking(false);
    }
  };

  const handleCancelDeleteParking = () => {
    setShowDeleteParkingConfirm(false);
    setParkingToDelete(null);
  };

  // Delete handlers for bicycle
  const handleConfirmDeleteBicycle = async () => {
    if (!bicycleToDelete) return;

    try {
      setIsDeletingBicycle(true);
      await api.post(`/workspaces/${workspaceId}/bicycle-spaces/release`, {
        spaceId: bicycleToDelete.spaceId
      });
      toast.success('Bicycle space removed successfully');
      setShowDeleteBicycleConfirm(false);
      setBicycleToDelete(null);
      // Reload bicycle allocation data
      await loadBicycleAllocation();
    } catch (error: any) {
      console.error('Error removing bicycle space:', error);
      toast.error(error.response?.data?.message || 'Failed to remove bicycle space');
    } finally {
      setIsDeletingBicycle(false);
    }
  };

  const handleCancelDeleteBicycle = () => {
    setShowDeleteBicycleConfirm(false);
    setBicycleToDelete(null);
  };

  // Delete handlers for locker
  const handleConfirmDeleteLocker = async () => {
    if (!lockerToDelete) return;

    try {
      setIsDeletingLocker(true);
      await api.post(`/workspaces/${workspaceId}/locker-spaces/release`, {
        spaceId: lockerToDelete.spaceId
      });
      toast.success('Locker removed successfully');
      setShowDeleteLockerConfirm(false);
      setLockerToDelete(null);
      // Reload locker allocation data
      await loadLockerAllocation();
    } catch (error: any) {
      console.error('Error removing locker:', error);
      toast.error(error.response?.data?.message || 'Failed to remove locker');
    } finally {
      setIsDeletingLocker(false);
    }
  };

  const handleCancelDeleteLocker = () => {
    setShowDeleteLockerConfirm(false);
    setLockerToDelete(null);
  };

  // Load summary data
  const loadSummaryData = async () => {
    try {
      setLoadingSummary(true);
      const response = await api.get(`/unit-deals/${workspaceId}/unit/${unitId}/saved-selections`);
      setSummaryData(response.data.data);

      // Load hero image if color collection has one
      if (response.data.data?.colorCollection?.heroImageId) {
        loadHeroImage(response.data.data.colorCollection.heroImageId);
      }
    } catch (err: any) {
      console.error('Error loading summary data:', err);
      toast.error('Failed to load summary data');
    } finally {
      setLoadingSummary(false);
    }
  };

  // Load hero image
  const loadHeroImage = async (imageId: string) => {
    try {
      setLoadingHeroImage(true);
      const url = await getImageUrlCached(workspaceId, imageId);
      setHeroImageUrl(url);
    } catch (err: any) {
      console.error('Error loading hero image:', err);
      // Don't show error toast as it's not critical
    } finally {
      setLoadingHeroImage(false);
    }
  };

  // Handle phase status change
  const handleStatusClick = (phase: string, status: string) => {
    // Don't allow changing if it's already the current status
    if (phaseStatuses[phase] === status) {
      return;
    }

    setPendingStatusChange({ phase, status });
    setShowStatusConfirmDialog(true);
  };

  const handleConfirmStatusChange = async () => {
    if (!pendingStatusChange) return;

    try {
      setUpdatingStatus(true);
      const { phase, status } = pendingStatusChange;

      await api.patch(`/unit-deals/${workspaceId}/unit/${unitId}/phase-status`, {
        phase,
        status
      });

      // Update local state
      setPhaseStatuses(prev => ({
        ...prev,
        [phase]: status
      }));

      toast.success('Phase status updated successfully');

      // Close dialog
      setShowStatusConfirmDialog(false);
      setPendingStatusChange(null);
    } catch (err: any) {
      console.error('Error updating phase status:', err);
      toast.error(err.response?.data?.message || 'Failed to update phase status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleCancelStatusChange = () => {
    setShowStatusConfirmDialog(false);
    setPendingStatusChange(null);
  };

  // Helper function to check if a package is incompatible with selected packages/options
  const isPackageIncompatible = (pkg: Package): boolean => {
    // Check if incompatible with any selected packages
    if (pkg.incompatiblePackages && pkg.incompatiblePackages.length > 0) {
      const incompatiblePackageIds = pkg.incompatiblePackages.map(p => p._id);
      const hasIncompatiblePackage = selectedPackageIds.some(selectedId =>
        incompatiblePackageIds.includes(selectedId)
      );
      if (hasIncompatiblePackage) return true;
    }

    // Check if incompatible with any selected options
    if (pkg.incompatiblePersonalizationOptions && pkg.incompatiblePersonalizationOptions.length > 0) {
      const incompatibleOptionIds = pkg.incompatiblePersonalizationOptions.map(o => o._id);
      const hasIncompatibleOption = selectedOptionIds.some(selectedId =>
        incompatibleOptionIds.includes(selectedId)
      );
      if (hasIncompatibleOption) return true;
    }

    return false;
  };

  // Helper function to check if an option is incompatible with selected packages/options
  const isOptionIncompatible = (option: Option): boolean => {
    // Check if incompatible with any selected packages
    if (option.incompatiblePackages && option.incompatiblePackages.length > 0) {
      const incompatiblePackageIds = option.incompatiblePackages.map(p => p._id);
      const hasIncompatiblePackage = selectedPackageIds.some(selectedId =>
        incompatiblePackageIds.includes(selectedId)
      );
      if (hasIncompatiblePackage) return true;
    }

    // Check if incompatible with any selected options
    if (option.incompatibleOptions && option.incompatibleOptions.length > 0) {
      const incompatibleOptionIds = option.incompatibleOptions.map(o => o._id);
      const hasIncompatibleOption = selectedOptionIds.some(selectedId =>
        incompatibleOptionIds.includes(selectedId)
      );
      if (hasIncompatibleOption) return true;
    }

    return false;
  };

  // Helper function to check if a package has unsatisfied dependencies
  const getPackageDependencyWarning = (pkg: Package): string | null => {
    // Check if parent package is required but not selected
    if (pkg.parentPackage && pkg.parentPackage.id) {
      const isParentSelected = selectedPackageIds.includes(pkg.parentPackage.id);
      if (!isParentSelected) {
        return `Requires parent package: ${pkg.parentPackage.name || pkg.parentPackage.id}`;
      }
    }

    // Note: Included options are automatically added when the package is selected,
    // so we don't need to check for them here

    return null;
  };

  // Helper function to check if an option has unsatisfied dependencies
  const getOptionDependencyWarning = (option: Option): string | null => {
    // Check if required packages are selected
    if (option.onlySoldWithPackages && option.onlySoldWithPackages.length > 0) {
      const missingPackages = option.onlySoldWithPackages.filter(
        pkg => !selectedPackageIds.includes(pkg._id)
      );
      if (missingPackages.length > 0) {
        const packageNames = missingPackages.map(p => p.name || p._id).join(', ');
        return `Requires packages: ${packageNames}`;
      }
    }

    // Check if required options are selected
    if (option.onlySoldWithOptions && option.onlySoldWithOptions.length > 0) {
      const missingOptions = option.onlySoldWithOptions.filter(
        opt => !selectedOptionIds.includes(opt._id)
      );
      if (missingOptions.length > 0) {
        const optionNames = missingOptions.map(o => o.name || o._id).join(', ');
        return `Requires options: ${optionNames}`;
      }
    }

    return null;
  };

  // Helper function to check if a phase tab should be enabled
  const isPhaseEnabled = (phase: string): boolean => {
    const phaseOrder = ['One', 'Two', 'Three', 'Four', 'Five'];
    const currentPhaseIndex = phaseOrder.indexOf(projectOngoingPhase);
    const checkPhaseIndex = phaseOrder.indexOf(phase);
    return checkPhaseIndex <= currentPhaseIndex;
  };

  // Helper function to check if selections can be modified based on phase status
  // When phase is provided (from Design Studio tab), check that specific phase's status
  // When phase is not provided (from Available Selections tab), use projectOngoingPhase
  const canModifySelections = (selectionType: 'color' | 'package' | 'option', phase?: string): boolean => {
    // Color collections can only be modified in Phase One
    if (selectionType === 'color') {
      const phaseOneStatus = phaseStatuses['One'] || 'NOT STARTED';
      return phaseOneStatus === 'NOT STARTED' || phaseOneStatus === 'OPEN';
    }

    // Packages and options can be modified based on the phase status
    // If phase is provided (Design Studio tab), check that phase's status
    // Otherwise use the project's ongoing phase (Available Selections tab)
    const checkPhase = phase || projectOngoingPhase;
    const currentPhaseStatus = phaseStatuses[checkPhase] || 'NOT STARTED';
    return currentPhaseStatus === 'NOT STARTED' || currentPhaseStatus === 'OPEN';
  };

  // Helper function to delete a phase selection
  const handleDeletePhaseSelection = async (selectionId: string, selectionType: string) => {
    try {
      setDeletingSelection(selectionId);

      let endpoint = '';
      let itemIdField = '';

      // Find the selection to get its itemId
      const selection = phaseSelections.find(s => s._id === selectionId);
      if (!selection) {
        toast.error('Selection not found');
        return;
      }

      // Determine endpoint based on selection type
      if (selectionType === 'color') {
        endpoint = `/unit-deals/${workspaceId}/unit/${unitId}/color-collections`;
        itemIdField = 'colorCollectionId';
      } else if (selectionType === 'package') {
        endpoint = `/unit-deals/${workspaceId}/unit/${unitId}/packages`;
        itemIdField = 'packageId';
      } else if (selectionType === 'option') {
        endpoint = `/unit-deals/${workspaceId}/unit/${unitId}/options`;
        itemIdField = 'optionId';
      }

      const response = await api.delete(endpoint, {
        data: { [itemIdField]: selection.itemId }
      });

      toast.success(response.data.message || 'Selection deleted successfully');

      // Reload phase selections
      await loadPhaseSelections(activePhaseTab);

      // Optionally call onSuccess callback
      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      console.error('Error deleting selection:', err);
      toast.error(err.response?.data?.message || 'Failed to delete selection');
    } finally {
      setDeletingSelection(null);
    }
  };

  // Handle Color Selection Amendment PDF generation
  const handleGenerateColorSelectionAmendment = async () => {
    try {
      setGeneratingPdf(true);

      // Get color collection from phase selections
      const colorSelection = phaseSelections.find(s => s.selectionType === 'color');

      if (!colorSelection) {
        toast.error('No color collection selected for this phase');
        setGeneratingPdf(false);
        return;
      }

      if (!unitDeal) {
        toast.error('Unit deal information not available');
        setGeneratingPdf(false);
        return;
      }

      // Fetch project logo, color collection hero image, and purchaser names from API
      let projectLogo: string | undefined = undefined;
      let colorSwatchImage: string | undefined = undefined;
      let purchasers: Array<{ name: string }> = [{ name: 'Purchaser' }];

      try {
        const imagesResponse = await api.get(
          `/unit-deals/${workspaceId}/unit/${unitId}/pdf-images?phase=${activePhaseTab}`
        );

        if (imagesResponse.data) {
          projectLogo = imagesResponse.data.projectLogo || undefined;
          colorSwatchImage = imagesResponse.data.colorCollectionHero || undefined;
          // Use purchasers from API if available
          if (imagesResponse.data.purchasers && imagesResponse.data.purchasers.length > 0) {
            purchasers = imagesResponse.data.purchasers;
          }
        }
      } catch (imgErr) {
        console.warn('Could not fetch images for PDF, continuing without them:', imgErr);
        // Fallback to splitting the deal string if API fails
        const purchaserNamesList = unitDeal.deal ? unitDeal.deal.split(',').map(name => name.trim()) : [];
        if (purchaserNamesList.length > 0) {
          purchasers = purchaserNamesList.map(name => ({ name }));
        }
      }

      // Generate the PDF
      generateColorSelectionAmendment({
        projectName: unitDeal.project || 'Project',
        unitNumber: unitDeal.unitNumber || '',
        level: '', // Can be added if available in future
        suiteNo: unitDeal.unitNumber || '',
        purchasers: purchasers,
        colorCollectionName: colorSelection.itemName,
        vendorName: 'Vendor', // Can be fetched from project/builder if needed
        projectLogo: projectLogo,
        colorSwatchImage: colorSwatchImage
      });

      toast.success('Color Selection Amendment PDF generated successfully');
    } catch (err: any) {
      console.error('Error generating Color Selection Amendment:', err);
      toast.error('Failed to generate PDF. Please try again.');
    } finally {
      setGeneratingPdf(false);
    }
  };

  // Template-based document generation handlers
  const handleOpenTemplateSelection = async (generationType: string) => {
    setSelectedGenerationType(generationType);
    setShowTemplateSelectModal(true);
    setSelectedTemplateId('');

    try {
      setLoadingTemplates(true);
      const response = await api.get(`/templates/${workspaceId}/project/${unitDeal?.projectId}`, {
        params: { type: generationType }
      });
      setAvailableTemplates(response.data.data);
    } catch (err: any) {
      console.error('Error fetching templates:', err);
      toast.error('Failed to load templates');
      setShowTemplateSelectModal(false);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleGenerateFromTemplate = async () => {
    if (!selectedTemplateId) {
      toast.error('Please select a template');
      return;
    }

    try {
      setGeneratingDocument(true);

      const response = await api.post(
        `/unit-deals/${workspaceId}/unit/${unitId}/generate-document`,
        {
          templateId: selectedTemplateId,
          templateType: selectedGenerationType
        },
        {
          responseType: 'blob' // Important for file download
        }
      );

      // Extract filename from Content-Disposition header
      const contentDisposition = response.headers['content-disposition'];
      let filename = `${selectedGenerationType}-${unitId}.pdf`; // Fallback filename

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, ''); // Remove any quotes
        }
      }

      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);

      toast.success('Document generated successfully');
      setShowTemplateSelectModal(false);
    } catch (err: any) {
      console.error('Error generating document:', err);
      toast.error('Failed to generate document. Please try again.');
    } finally {
      setGeneratingDocument(false);
    }
  };

  const getTemplateTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      'color_selection_amendment': 'Color Selection Amendment',
      'design_studio_amendment': 'Design Studio Amendment',
      'revised_design_studio_amendment': 'Revised Design Studio Amendment',
      'design_studio_invoice': 'Design Studio Invoice',
      'design_studio_summary_invoice': 'Design Studio Summary Invoice',
      'parking_selection_amendment': 'Parking Selection Amendment',
      'parking_invoice': 'Parking Invoice',
      'locker_selection_amendment': 'Locker Selection Amendment',
      'locker_invoice': 'Locker Invoice',
      'bicycle_selection_amendment': 'Bicycle Selection Amendment',
      'bicycle_invoice': 'Bicycle Invoice'
    };
    return labels[type] || type;
  };

  const getAvailableDocumentTypes = () => {
    const allTypes = [
      { type: 'color_selection_amendment', label: 'Color Selection Amendment', description: 'Document for color collection selections', tab: 'design-studio', phase: 'One' },
      { type: 'design_studio_amendment', label: 'Design Studio Amendment', description: 'Standard design studio amendment document', tab: 'design-studio', phase: 'all' },
      { type: 'revised_design_studio_amendment', label: 'Revised Design Studio Amendment', description: 'Revised version of design studio amendment', tab: 'design-studio', phase: 'all' },
      { type: 'design_studio_invoice', label: 'Design Studio Invoice', description: 'Invoice for design studio selections', tab: 'design-studio', phase: 'all' },
      { type: 'design_studio_summary_invoice', label: 'Design Studio Summary Invoice', description: 'Summary invoice for all design studio selections', tab: 'summary', phase: 'all' },
      { type: 'parking_selection_amendment', label: 'Parking Selection Amendment', description: 'Amendment for parking selections', tab: 'parking-selection', phase: 'all' },
      { type: 'parking_invoice', label: 'Parking Invoice', description: 'Invoice for parking selections', tab: 'parking-selection', phase: 'all' },
      { type: 'locker_selection_amendment', label: 'Locker Selection Amendment', description: 'Amendment for locker selections', tab: 'locker-selection', phase: 'all' },
      { type: 'locker_invoice', label: 'Locker Invoice', description: 'Invoice for locker selections', tab: 'locker-selection', phase: 'all' },
      { type: 'bicycle_selection_amendment', label: 'Bicycle Selection Amendment', description: 'Amendment for bicycle selections', tab: 'bicycle-parking-selection', phase: 'all' },
      { type: 'bicycle_invoice', label: 'Bicycle Invoice', description: 'Invoice for bicycle selections', tab: 'bicycle-parking-selection', phase: 'all' }
    ];

    // Filter based on active tab
    let filteredTypes = allTypes.filter(docType => {
      const tabs = docType.tab.split(',');
      return tabs.includes(activeTab);
    });

    // For design-studio tab, further filter by phase and exclude summary invoice
    if (activeTab === 'design-studio') {
      filteredTypes = filteredTypes.filter(docType => {
        // Exclude summary invoice from design-studio tab
        if (docType.type === 'design_studio_summary_invoice') return false;
        if (docType.phase === 'all') return true;
        if (docType.phase === 'One' && activePhaseTab === 'One') return true;
        return false;
      });
    }

    // For summary tab, show only Design Studio Summary Invoice
    if (activeTab === 'summary') {
      filteredTypes = filteredTypes.filter(docType =>
        docType.type === 'design_studio_summary_invoice'
      );
    }

    return filteredTypes;
  };

  return (
    <div className="unit-deal-details-slider">
      {/* Header */}
      <div className="unit-deal-details-header">
        <div className="unit-deal-details-header-left">
          <button
            onClick={onClose}
            className="unit-deal-details-back-btn"
            aria-label="Go back"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="unit-deal-details-title">
            <h2>
              {loading ? 'Loading...' : unitDeal ? `${unitDeal.project} - Unit ${unitDeal.unitNumber}` : 'Unit Details'}
            </h2>
            <p>
              {loading ? '' : unitDeal ? `Unit Status: ${unitDeal.unitStatus} | Deal Status: ${unitDeal.dealStatus || 'N/A'}` : 'Unit and Deal Management Details'}
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="unit-deal-details-close-btn"
          aria-label="Close"
        >
          <X size={24} />
        </button>
      </div>

      {/* Tabs */}
      {!loading && !error && (
        <div className="unit-deal-details-tabs">
          <button
            className={`unit-deal-details-tab ${activeTab === 'available-selections' ? 'active' : ''}`}
            onClick={() => setActiveTab('available-selections')}
          >
            Available Selections
          </button>
          <button
            className={`unit-deal-details-tab ${activeTab === 'design-studio' ? 'active' : ''}`}
            onClick={() => setActiveTab('design-studio')}
          >
            Design Studio
          </button>
          <button
            className={`unit-deal-details-tab ${activeTab === 'parking-selection' ? 'active' : ''}`}
            onClick={() => setActiveTab('parking-selection')}
          >
            Parking Selection
          </button>
          <button
            className={`unit-deal-details-tab ${activeTab === 'bicycle-parking-selection' ? 'active' : ''}`}
            onClick={() => setActiveTab('bicycle-parking-selection')}
          >
            Bicycle Parking Selection
          </button>
          <button
            className={`unit-deal-details-tab ${activeTab === 'locker-selection' ? 'active' : ''}`}
            onClick={() => setActiveTab('locker-selection')}
          >
            Locker Selection
          </button>
          <button
            className={`unit-deal-details-tab ${activeTab === 'layout-customizations' ? 'active' : ''}`}
            onClick={() => setActiveTab('layout-customizations')}
          >
            Layout Customizations
          </button>
          <button
            className={`unit-deal-details-tab ${activeTab === 'summary' ? 'active' : ''}`}
            onClick={() => setActiveTab('summary')}
          >
            Summary
          </button>
          <button
            className={`unit-deal-details-tab ${activeTab === 'documents' ? 'active' : ''}`}
            onClick={() => setActiveTab('documents')}
          >
            Documents
          </button>
        </div>
      )}

      {/* Content */}
      <div className="unit-deal-details-content">
        {loading ? (
          <div className="unit-deal-details-loading">
            <div className="builders-loading-spinner" />
          </div>
        ) : error ? (
          <div className="unit-deal-details-error">
            <p>{error}</p>
          </div>
        ) : (
          <>
            {activeTab === 'available-selections' && (
              <div style={{ gridColumn: '1 / -1', padding: 0 }}>
                {/* Color Collections Section */}
                <div className="unit-deal-section">
                  <div className="unit-deal-section-header">
                    <h3>Color Collection</h3>
                  </div>

                  {loadingColorCollections ? (
                    <div style={{ padding: '40px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <div className="builders-loading-spinner" />
                    </div>
                  ) : colorCollections.length === 0 ? (
                    <div className="unit-deal-empty-state">
                      <p>No color collections available for this unit</p>
                    </div>
                  ) : (
                    <div className="data-table-container">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th style={{ textAlign: 'right' }}>Cost</th>
                            <th style={{ textAlign: 'right' }}>Price</th>
                            <th style={{ textAlign: 'center' }}>Info</th>
                            <th style={{ textAlign: 'center' }}>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {colorCollections.map((collection) => (
                            <tr key={collection._id}>
                              <td className="data-table-primary-cell">{collection.colorCollectionName}</td>
                              <td style={{ textAlign: 'right' }}>${collection.cost.toFixed(2)}</td>
                              <td style={{ textAlign: 'right' }}>${collection.price.toFixed(2)}</td>
                              <td style={{ textAlign: 'center' }}>
                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                  <button
                                    className="btn btn-sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setInfoModalData({
                                        title: collection.colorCollectionName,
                                        description: collection.description,
                                        constructionNotes: collection.constructionNotes
                                      });
                                      setShowInfoModal(true);
                                    }}
                                    title="View Details"
                                    style={{
                                      width: '32px',
                                      height: '32px',
                                      borderRadius: '50%',
                                      padding: '0',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      backgroundColor: '#48182F',
                                      color: 'white',
                                      border: 'none',
                                      fontSize: '14px',
                                      fontWeight: '600',
                                      fontStyle: 'italic',
                                      cursor: 'pointer',
                                      transition: 'opacity 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                                  >
                                    i
                                  </button>
                                </div>
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                {selectedColorCollectionId === collection.colorCollectionId ? (
                                  <button
                                    className="btn btn-sm btn-success"
                                    disabled
                                    style={{
                                      backgroundColor: '#10B981',
                                      color: 'white',
                                      cursor: 'default',
                                      opacity: 1
                                    }}
                                  >
                                    Selected
                                  </button>
                                ) : (
                                  <>
                                    {!canModifySelections('color') ? (
                                      <button
                                        className="btn btn-sm btn-secondary"
                                        disabled
                                        style={{
                                          opacity: 0.6,
                                          cursor: 'not-allowed'
                                        }}
                                        title={projectOngoingPhase !== 'One'
                                          ? "Color collection can only be modified in Phase One"
                                          : `Color collection can only be modified when Phase One status is "NOT STARTED" or "OPEN". Current status: ${phaseStatuses['One']}`
                                        }
                                      >
                                        Locked
                                      </button>
                                    ) : (
                                      <button
                                        className="btn btn-sm btn-primary"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleAddColorCollectionClick(collection.colorCollectionId);
                                        }}
                                        disabled={addingColorCollection === collection.colorCollectionId}
                                        style={{
                                          opacity: addingColorCollection === collection.colorCollectionId ? 0.7 : 1
                                        }}
                                      >
                                        {addingColorCollection === collection.colorCollectionId ? 'Adding...' : 'Add'}
                                      </button>
                                    )}
                                  </>
                                )}
                              </td>
                            </tr>
                          ))}
                          <tr style={{ fontWeight: '600', borderTop: '2px solid #E5E7EB', backgroundColor: '#F9FAFB' }}>
                            <td className="data-table-primary-cell">Total</td>
                            <td style={{ textAlign: 'right' }}>
                              ${colorCollections
                                .filter(col => selectedColorCollectionId === col.colorCollectionId)
                                .reduce((sum, col) => sum + col.cost, 0)
                                .toFixed(2)}
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              ${colorCollections
                                .filter(col => selectedColorCollectionId === col.colorCollectionId)
                                .reduce((sum, col) => sum + col.price, 0)
                                .toFixed(2)}
                            </td>
                            <td></td>
                            <td></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Packages Section */}
                {selectedColorCollectionId && (
                  <div className="unit-deal-section">
                    <div className="unit-deal-section-header">
                      <h3>Packages</h3>
                    </div>

                    {loadingPackages ? (
                      <div style={{ padding: '40px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <div className="builders-loading-spinner" />
                      </div>
                    ) : packages.length === 0 ? (
                      <div className="unit-deal-empty-state">
                        <p>No packages available for this color collection</p>
                      </div>
                    ) : (
                      <div className="data-table-container">
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Location</th>
                              <th style={{ textAlign: 'right' }}>Cost</th>
                              <th style={{ textAlign: 'right' }}>Price</th>
                              <th style={{ textAlign: 'center' }}>Info</th>
                              <th style={{ textAlign: 'center' }}>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {packages.map((pkg) => {
                              const isIncompatible = isPackageIncompatible(pkg);
                              const isSelected = selectedPackageIds.includes(pkg.packageId);
                              const dependencyWarning = getPackageDependencyWarning(pkg);
                              const hasUnsatisfiedDependency = dependencyWarning !== null;

                              // Hide incompatible packages that are not selected
                              if (isIncompatible && !isSelected) {
                                return null;
                              }

                              return (
                                <tr key={pkg._id} style={{ opacity: isIncompatible ? 0.5 : 1 }}>
                                  <td className="data-table-primary-cell">
                                    {pkg.packageName}
                                    {isIncompatible && (
                                      <span style={{
                                        marginLeft: '8px',
                                        fontSize: '11px',
                                        color: '#EF4444',
                                        fontWeight: '600'
                                      }}>
                                        (Incompatible)
                                      </span>
                                    )}
                                    {hasUnsatisfiedDependency && !isSelected && (
                                      <div style={{
                                        fontSize: '11px',
                                        color: '#F59E0B',
                                        marginTop: '4px',
                                        fontStyle: 'italic'
                                      }}>
                                        {dependencyWarning}
                                      </div>
                                    )}
                                    {pkg.includedOptions && pkg.includedOptions.length > 0 && !isSelected && (
                                      <div style={{
                                        fontSize: '11px',
                                        color: '#10B981',
                                        marginTop: '4px',
                                        fontStyle: 'italic'
                                      }}>
                                        Includes: {pkg.includedOptions.map(o => o.name || o._id).join(', ')}
                                      </div>
                                    )}
                                  </td>
                                  <td>{pkg.location || '-'}</td>
                                  <td style={{ textAlign: 'right' }}>${pkg.cost.toFixed(2)}</td>
                                  <td style={{ textAlign: 'right' }}>${pkg.price.toFixed(2)}</td>
                                  <td style={{ textAlign: 'center' }}>
                                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                      <button
                                        className="btn btn-sm"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setInfoModalData({
                                            title: pkg.packageName,
                                            description: pkg.description,
                                            constructionNotes: pkg.constructionNotes
                                          });
                                          setShowInfoModal(true);
                                        }}
                                        title="View Details"
                                        style={{
                                          width: '32px',
                                          height: '32px',
                                          borderRadius: '50%',
                                          padding: '0',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          backgroundColor: '#48182F',
                                          color: 'white',
                                          border: 'none',
                                          fontSize: '14px',
                                          fontWeight: '600',
                                          fontStyle: 'italic',
                                          cursor: 'pointer',
                                          transition: 'opacity 0.2s'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                                        onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                                      >
                                        i
                                      </button>
                                    </div>
                                  </td>
                                  <td style={{ textAlign: 'center' }}>
                                    {isSelected ? (
                                      <button
                                        className="btn btn-sm btn-danger"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (!canModifySelections('package')) {
                                            toast.error(`Packages can only be modified when ${projectOngoingPhase} status is "NOT STARTED" or "OPEN"`);
                                          } else {
                                            handleRemovePackage(pkg.packageId);
                                          }
                                        }}
                                        disabled={!canModifySelections('package') || removingPackage === pkg.packageId}
                                        style={{
                                          backgroundColor: '#EF4444',
                                          color: 'white',
                                          opacity: (!canModifySelections('package') || removingPackage === pkg.packageId) ? 0.7 : 1,
                                          cursor: !canModifySelections('package') ? 'not-allowed' : 'pointer'
                                        }}
                                        title={!canModifySelections('package') ? `Locked: Phase ${projectOngoingPhase} status is ${phaseStatuses[projectOngoingPhase]}` : ''}
                                      >
                                        {removingPackage === pkg.packageId ? 'Removing...' : 'Remove'}
                                      </button>
                                    ) : (
                                      <button
                                        className="btn btn-sm btn-primary"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (!canModifySelections('package')) {
                                            toast.error(`Packages can only be modified when ${projectOngoingPhase} status is "NOT STARTED" or "OPEN"`);
                                          } else if (hasUnsatisfiedDependency) {
                                            toast.warning(dependencyWarning);
                                          } else {
                                            handleAddPackage(pkg.packageId);
                                          }
                                        }}
                                        disabled={!canModifySelections('package') || isIncompatible || hasUnsatisfiedDependency || addingPackage === pkg.packageId}
                                        style={{
                                          opacity: (!canModifySelections('package') || isIncompatible || hasUnsatisfiedDependency || addingPackage === pkg.packageId) ? 0.7 : 1,
                                          cursor: (!canModifySelections('package') || isIncompatible || hasUnsatisfiedDependency || addingPackage === pkg.packageId) ? 'not-allowed' : 'pointer'
                                        }}
                                        title={!canModifySelections('package') ? `Locked: ${projectOngoingPhase} status is ${phaseStatuses[projectOngoingPhase]}` : ''}
                                      >
                                        {addingPackage === pkg.packageId ? 'Adding...' : 'Add'}
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                            <tr style={{ fontWeight: '600', borderTop: '2px solid #E5E7EB', backgroundColor: '#F9FAFB' }}>
                              <td className="data-table-primary-cell">Total</td>
                              <td></td>
                              <td style={{ textAlign: 'right' }}>
                                ${packages
                                  .filter(pkg => selectedPackageIds.includes(pkg.packageId))
                                  .reduce((sum, pkg) => sum + pkg.cost, 0)
                                  .toFixed(2)}
                              </td>
                              <td style={{ textAlign: 'right' }}>
                                ${packages
                                  .filter(pkg => selectedPackageIds.includes(pkg.packageId))
                                  .reduce((sum, pkg) => sum + pkg.price, 0)
                                  .toFixed(2)}
                              </td>
                              <td></td>
                              <td></td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {/* Options Section */}
                {selectedColorCollectionId && (
                  <div className="unit-deal-section">
                    <div className="unit-deal-section-header">
                      <h3>Options</h3>
                    </div>

                    {loadingOptions ? (
                      <div style={{ padding: '40px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <div className="builders-loading-spinner" />
                      </div>
                    ) : options.length === 0 ? (
                      <div className="unit-deal-empty-state">
                        <p>No options available for this color collection</p>
                      </div>
                    ) : (
                      <div className="data-table-container">
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Location</th>
                              <th>Category</th>
                              <th style={{ textAlign: 'right' }}>Cost</th>
                              <th style={{ textAlign: 'right' }}>Price</th>
                              <th style={{ textAlign: 'center' }}>Info</th>
                              <th style={{ textAlign: 'center' }}>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {options.map((option) => {
                              const isIncompatible = isOptionIncompatible(option);
                              const isSelected = selectedOptionIds.includes(option.optionId);
                              const dependencyWarning = getOptionDependencyWarning(option);
                              const hasUnsatisfiedDependency = dependencyWarning !== null;

                              // Hide incompatible options that are not selected
                              if (isIncompatible && !isSelected) {
                                return null;
                              }

                              return (
                                <tr key={option._id} style={{ opacity: isIncompatible ? 0.5 : 1 }}>
                                  <td className="data-table-primary-cell">
                                    {option.optionName}
                                    {isIncompatible && (
                                      <span style={{
                                        marginLeft: '8px',
                                        fontSize: '11px',
                                        color: '#EF4444',
                                        fontWeight: '600'
                                      }}>
                                        (Incompatible)
                                      </span>
                                    )}
                                    {hasUnsatisfiedDependency && !isSelected && (
                                      <div style={{
                                        fontSize: '11px',
                                        color: '#F59E0B',
                                        marginTop: '4px',
                                        fontStyle: 'italic'
                                      }}>
                                        {dependencyWarning}
                                      </div>
                                    )}
                                  </td>
                                  <td>{option.location || '-'}</td>
                                  <td>{option.categoryName || '-'}</td>
                                  <td style={{ textAlign: 'right' }}>${option.cost.toFixed(2)}</td>
                                  <td style={{ textAlign: 'right' }}>${option.price.toFixed(2)}</td>
                                  <td style={{ textAlign: 'center' }}>
                                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                      <button
                                        className="btn btn-sm"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setInfoModalData({
                                            title: option.optionName,
                                            description: option.description,
                                            constructionNotes: option.constructionNotes
                                          });
                                          setShowInfoModal(true);
                                        }}
                                        title="View Details"
                                        style={{
                                          width: '32px',
                                          height: '32px',
                                          borderRadius: '50%',
                                          padding: '0',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          backgroundColor: '#48182F',
                                          color: 'white',
                                          border: 'none',
                                          fontSize: '14px',
                                          fontWeight: '600',
                                          fontStyle: 'italic',
                                          cursor: 'pointer',
                                          transition: 'opacity 0.2s'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                                        onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                                      >
                                        i
                                      </button>
                                    </div>
                                  </td>
                                  <td style={{ textAlign: 'center' }}>
                                    {isSelected ? (
                                      <button
                                        className="btn btn-sm btn-danger"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (!canModifySelections('option')) {
                                            toast.error(`Options can only be modified when ${projectOngoingPhase} status is "NOT STARTED" or "OPEN"`);
                                          } else {
                                            handleRemoveOption(option.optionId);
                                          }
                                        }}
                                        disabled={!canModifySelections('option') || removingOption === option.optionId}
                                        style={{
                                          backgroundColor: '#EF4444',
                                          color: 'white',
                                          opacity: (!canModifySelections('option') || removingOption === option.optionId) ? 0.7 : 1,
                                          cursor: !canModifySelections('option') ? 'not-allowed' : 'pointer'
                                        }}
                                        title={!canModifySelections('option') ? `Locked: Phase ${projectOngoingPhase} status is ${phaseStatuses[projectOngoingPhase]}` : ''}
                                      >
                                        {removingOption === option.optionId ? 'Removing...' : 'Remove'}
                                      </button>
                                    ) : (
                                      <button
                                        className="btn btn-sm btn-primary"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (!canModifySelections('option')) {
                                            toast.error(`Options can only be modified when ${projectOngoingPhase} status is "NOT STARTED" or "OPEN"`);
                                          } else if (hasUnsatisfiedDependency) {
                                            toast.warning(dependencyWarning);
                                          } else {
                                            handleAddOption(option.optionId);
                                          }
                                        }}
                                        disabled={!canModifySelections('option') || isIncompatible || hasUnsatisfiedDependency || addingOption === option.optionId}
                                        style={{
                                          opacity: (!canModifySelections('option') || isIncompatible || hasUnsatisfiedDependency || addingOption === option.optionId) ? 0.7 : 1,
                                          cursor: (!canModifySelections('option') || isIncompatible || hasUnsatisfiedDependency || addingOption === option.optionId) ? 'not-allowed' : 'pointer'
                                        }}
                                        title={!canModifySelections('option') ? `Locked: Phase ${projectOngoingPhase} status is ${phaseStatuses[projectOngoingPhase]}` : ''}
                                      >
                                        {addingOption === option.optionId ? 'Adding...' : 'Add'}
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                            <tr style={{ fontWeight: '600', borderTop: '2px solid #E5E7EB', backgroundColor: '#F9FAFB' }}>
                              <td className="data-table-primary-cell">Total</td>
                              <td></td>
                              <td></td>
                              <td style={{ textAlign: 'right' }}>
                                ${options
                                  .filter(opt => selectedOptionIds.includes(opt.optionId))
                                  .reduce((sum, opt) => sum + opt.cost, 0)
                                  .toFixed(2)}
                              </td>
                              <td style={{ textAlign: 'right' }}>
                                ${options
                                  .filter(opt => selectedOptionIds.includes(opt.optionId))
                                  .reduce((sum, opt) => sum + opt.price, 0)
                                  .toFixed(2)}
                              </td>
                              <td></td>
                              <td></td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            {activeTab === 'design-studio' && (
              <div style={{ gridColumn: '1 / -1', padding: 0 }}>
                {/* Phase Tabs */}
                <div className="unit-deal-details-tabs" style={{ borderBottom: '1px solid #E5E7EB', backgroundColor: '#F9FAFB' }}>
                  {['One', 'Two', 'Three', 'Four', 'Five'].map((phase) => (
                    <button
                      key={phase}
                      className={`unit-deal-details-tab ${activePhaseTab === phase ? 'active' : ''}`}
                      onClick={() => {
                        if (isPhaseEnabled(phase)) {
                          setActivePhaseTab(phase);
                        }
                      }}
                      disabled={!isPhaseEnabled(phase)}
                      style={{
                        opacity: isPhaseEnabled(phase) ? 1 : 0.5,
                        cursor: isPhaseEnabled(phase) ? 'pointer' : 'not-allowed'
                      }}
                    >
                      Phase {phase}
                    </button>
                  ))}
                </div>

                {/* Phase Content */}
                {loadingPhaseSelections ? (
                  <div style={{ padding: '40px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <div className="builders-loading-spinner" />
                  </div>
                ) : (
                  <>
                    {/* Phase Stage Progress Card */}
                    <div style={{
                      background: 'white',
                      padding: '24px 40px',
                      borderBottom: '1px solid #E5E7EB',
                      overflowX: 'auto',
                      overflowY: 'hidden'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0,
                        position: 'relative',
                        minWidth: 'max-content'
                      }}>
                        {['NOT STARTED', 'OPEN', 'SUBMITTED', 'SELECTION FINAL', 'WAITING FOR PAYMENT', 'COMPLETED', 'CANCELLED - BY CUSTOMER', 'CANCELLED - NO PAYMENT'].map((stage, index) => {
                          // Get the current phase status
                          const currentStatus = phaseStatuses[activePhaseTab] || 'NOT STARTED';
                          const isActive = stage === currentStatus;
                          const isFirst = index === 0;
                          const isLast = index === 7;

                          return (
                            <div
                              key={stage}
                              onClick={() => handleStatusClick(activePhaseTab, stage)}
                              style={{
                                flexShrink: 0,
                                minWidth: '220px',
                                padding: '10px 24px',
                                paddingLeft: isFirst ? '28px' : '24px',
                                paddingRight: isLast ? '28px' : '24px',
                                background: isActive ? '#D4CBC4' : '#48182F',
                                color: isActive ? '#48182F' : 'white',
                                fontSize: '13px',
                                fontWeight: '600',
                                textAlign: 'center',
                                position: 'relative',
                                clipPath: isFirst
                                  ? 'polygon(0 0, calc(100% - 20px) 0, 100% 50%, calc(100% - 20px) 100%, 0 100%)'
                                  : isLast
                                  ? 'polygon(0 0, 100% 0, 100% 100%, 0 100%, 20px 50%)'
                                  : 'polygon(0 0, calc(100% - 20px) 0, 100% 50%, calc(100% - 20px) 100%, 0 100%, 20px 50%)',
                                marginRight: isLast ? '0' : '-20px',
                                transform: isActive ? 'scale(1.05)' : 'scale(1)',
                                zIndex: isActive ? 1 : 0,
                                transition: 'all 0.2s',
                                whiteSpace: 'nowrap',
                                cursor: 'pointer'
                              }}
                            >
                              {stage}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Generate Document Button Bar */}
                    <div style={{
                      background: 'white',
                      padding: '16px 40px',
                      borderBottom: '1px solid #E5E7EB',
                      display: 'flex',
                      justifyContent: 'flex-end',
                      alignItems: 'center'
                    }}>
                      <button
                        className="generate-doc-btn"
                        onClick={() => setShowDocumentTypeModal(true)}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                          <polyline points="14 2 14 8 20 8"></polyline>
                          <line x1="16" y1="13" x2="8" y2="13"></line>
                          <line x1="16" y1="17" x2="8" y2="17"></line>
                          <polyline points="10 9 9 9 8 9"></polyline>
                        </svg>
                        Generate Document
                      </button>
                    </div>

                    {phaseSelections.length === 0 ? (
                      <div className="unit-deal-empty-state" style={{ padding: '40px' }}>
                        <p>No selections made for Phase {activePhaseTab}</p>
                      </div>
                    ) : (
                      <>
                    {/* Color Collections for this phase */}
                    {phaseSelections.filter(s => s.selectionType === 'color').length > 0 && (
                      <div className="unit-deal-section">
                        <div className="unit-deal-section-header">
                          <h3>Color Collection</h3>
                        </div>
                        <div className="data-table-container">
                          <table className="data-table">
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th style={{ textAlign: 'right' }}>Cost</th>
                                <th style={{ textAlign: 'right' }}>Price</th>
                                <th style={{ textAlign: 'center' }}>Info</th>
                                <th style={{ textAlign: 'center' }}>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {phaseSelections
                                .filter(s => s.selectionType === 'color')
                                .map((selection) => (
                                  <tr key={selection._id}>
                                    <td className="data-table-primary-cell">{selection.itemName}</td>
                                    <td style={{ textAlign: 'right' }}>${selection.cost.toFixed(2)}</td>
                                    <td style={{ textAlign: 'right' }}>${selection.price.toFixed(2)}</td>
                                    <td style={{ textAlign: 'center' }}>
                                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                        <button
                                          className="btn btn-sm"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setInfoModalData({
                                              title: selection.itemName,
                                              description: selection.description,
                                              constructionNotes: selection.constructionNotes
                                            });
                                            setShowInfoModal(true);
                                          }}
                                          title="View Details"
                                          style={{
                                            width: '32px',
                                            height: '32px',
                                            borderRadius: '50%',
                                            padding: '0',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: '#48182F',
                                            color: 'white',
                                            border: 'none',
                                            fontSize: '14px',
                                            fontWeight: '600',
                                            fontStyle: 'italic',
                                            cursor: 'pointer',
                                            transition: 'opacity 0.2s'
                                          }}
                                          onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                                          onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                                        >
                                          i
                                        </button>
                                      </div>
                                    </td>
                                    <td style={{ textAlign: 'center' }}>
                                      <button
                                        className="btn btn-sm btn-danger"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (!canModifySelections('color', activePhaseTab)) {
                                            toast.error(`Color collection can only be modified when Phase One status is "NOT STARTED" or "OPEN". Current status: ${phaseStatuses['One']}`);
                                          } else {
                                            handleDeletePhaseSelection(selection._id, selection.selectionType);
                                          }
                                        }}
                                        disabled={!canModifySelections('color', activePhaseTab) || deletingSelection === selection._id}
                                        style={{
                                          backgroundColor: '#EF4444',
                                          color: 'white',
                                          opacity: (!canModifySelections('color', activePhaseTab) || deletingSelection === selection._id) ? 0.7 : 1,
                                          cursor: !canModifySelections('color', activePhaseTab) ? 'not-allowed' : 'pointer'
                                        }}
                                        title={!canModifySelections('color', activePhaseTab) ? `Locked: Phase One status is ${phaseStatuses['One']}` : ''}
                                      >
                                        {deletingSelection === selection._id ? 'Deleting...' : 'Delete'}
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              <tr style={{ fontWeight: '600', borderTop: '2px solid #E5E7EB', backgroundColor: '#F9FAFB' }}>
                                <td className="data-table-primary-cell">Total</td>
                                <td style={{ textAlign: 'right' }}>
                                  ${phaseSelections
                                    .filter(s => s.selectionType === 'color')
                                    .reduce((sum, s) => sum + s.cost, 0)
                                    .toFixed(2)}
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                  ${phaseSelections
                                    .filter(s => s.selectionType === 'color')
                                    .reduce((sum, s) => sum + s.price, 0)
                                    .toFixed(2)}
                                </td>
                                <td></td>
                                <td></td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Packages for this phase */}
                    {phaseSelections.filter(s => s.selectionType === 'package').length > 0 && (
                      <div className="unit-deal-section">
                        <div className="unit-deal-section-header">
                          <h3>Packages</h3>
                        </div>
                        <div className="data-table-container">
                          <table className="data-table">
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Location</th>
                                <th style={{ textAlign: 'right' }}>Cost</th>
                                <th style={{ textAlign: 'right' }}>Price</th>
                                <th style={{ textAlign: 'center' }}>Info</th>
                                <th style={{ textAlign: 'center' }}>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {phaseSelections
                                .filter(s => s.selectionType === 'package')
                                .map((selection) => (
                                  <tr key={selection._id}>
                                    <td className="data-table-primary-cell">{selection.itemName}</td>
                                    <td>{selection.location || '-'}</td>
                                    <td style={{ textAlign: 'right' }}>${selection.cost.toFixed(2)}</td>
                                    <td style={{ textAlign: 'right' }}>${selection.price.toFixed(2)}</td>
                                    <td style={{ textAlign: 'center' }}>
                                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                        <button
                                          className="btn btn-sm"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setInfoModalData({
                                              title: selection.itemName,
                                              description: selection.description,
                                              constructionNotes: selection.constructionNotes
                                            });
                                            setShowInfoModal(true);
                                          }}
                                          title="View Details"
                                          style={{
                                            width: '32px',
                                            height: '32px',
                                            borderRadius: '50%',
                                            padding: '0',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: '#48182F',
                                            color: 'white',
                                            border: 'none',
                                            fontSize: '14px',
                                            fontWeight: '600',
                                            fontStyle: 'italic',
                                            cursor: 'pointer',
                                            transition: 'opacity 0.2s'
                                          }}
                                          onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                                          onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                                        >
                                          i
                                        </button>
                                      </div>
                                    </td>
                                    <td style={{ textAlign: 'center' }}>
                                      <button
                                        className="btn btn-sm btn-danger"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (!canModifySelections('package', activePhaseTab)) {
                                            toast.error(`Packages can only be modified when Phase ${activePhaseTab} status is "NOT STARTED" or "OPEN". Current status: ${phaseStatuses[activePhaseTab]}`);
                                          } else {
                                            handleDeletePhaseSelection(selection._id, selection.selectionType);
                                          }
                                        }}
                                        disabled={!canModifySelections('package', activePhaseTab) || deletingSelection === selection._id}
                                        style={{
                                          backgroundColor: '#EF4444',
                                          color: 'white',
                                          opacity: (!canModifySelections('package', activePhaseTab) || deletingSelection === selection._id) ? 0.7 : 1,
                                          cursor: !canModifySelections('package', activePhaseTab) ? 'not-allowed' : 'pointer'
                                        }}
                                        title={!canModifySelections('package', activePhaseTab) ? `Locked: Phase ${activePhaseTab} status is ${phaseStatuses[activePhaseTab]}` : ''}
                                      >
                                        {deletingSelection === selection._id ? 'Deleting...' : 'Delete'}
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              <tr style={{ fontWeight: '600', borderTop: '2px solid #E5E7EB', backgroundColor: '#F9FAFB' }}>
                                <td className="data-table-primary-cell">Total</td>
                                <td></td>
                                <td style={{ textAlign: 'right' }}>
                                  ${phaseSelections
                                    .filter(s => s.selectionType === 'package')
                                    .reduce((sum, s) => sum + s.cost, 0)
                                    .toFixed(2)}
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                  ${phaseSelections
                                    .filter(s => s.selectionType === 'package')
                                    .reduce((sum, s) => sum + s.price, 0)
                                    .toFixed(2)}
                                </td>
                                <td></td>
                                <td></td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Options for this phase */}
                    {phaseSelections.filter(s => s.selectionType === 'option').length > 0 && (
                      <div className="unit-deal-section">
                        <div className="unit-deal-section-header">
                          <h3>Options</h3>
                        </div>
                        <div className="data-table-container">
                          <table className="data-table">
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Location</th>
                                <th>Category</th>
                                <th style={{ textAlign: 'right' }}>Cost</th>
                                <th style={{ textAlign: 'right' }}>Price</th>
                                <th style={{ textAlign: 'center' }}>Quantity</th>
                                <th style={{ textAlign: 'right' }}>Total</th>
                                <th style={{ textAlign: 'center' }}>Info</th>
                                <th style={{ textAlign: 'center' }}>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {phaseSelections
                                .filter(s => s.selectionType === 'option')
                                .map((selection) => (
                                  <tr key={selection._id}>
                                    <td className="data-table-primary-cell">{selection.itemName}</td>
                                    <td>{selection.location || '-'}</td>
                                    <td>{selection.categoryName || '-'}</td>
                                    <td style={{ textAlign: 'right' }}>${selection.cost.toFixed(2)}</td>
                                    <td style={{ textAlign: 'right' }}>${selection.price.toFixed(2)}</td>
                                    <td style={{ textAlign: 'center' }}>{selection.quantity || 1}</td>
                                    <td style={{ textAlign: 'right' }}>${(selection.price * (selection.quantity || 1)).toFixed(2)}</td>
                                    <td style={{ textAlign: 'center' }}>
                                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                        <button
                                          className="btn btn-sm"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setInfoModalData({
                                              title: selection.itemName,
                                              description: selection.description,
                                              constructionNotes: selection.constructionNotes
                                            });
                                            setShowInfoModal(true);
                                          }}
                                          title="View Details"
                                          style={{
                                            width: '32px',
                                            height: '32px',
                                            borderRadius: '50%',
                                            padding: '0',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: '#48182F',
                                            color: 'white',
                                            border: 'none',
                                            fontSize: '14px',
                                            fontWeight: '600',
                                            fontStyle: 'italic',
                                            cursor: 'pointer',
                                            transition: 'opacity 0.2s'
                                          }}
                                          onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                                          onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                                        >
                                          i
                                        </button>
                                      </div>
                                    </td>
                                    <td style={{ textAlign: 'center' }}>
                                      <button
                                        className="btn btn-sm btn-danger"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (!canModifySelections('option', activePhaseTab)) {
                                            toast.error(`Options can only be modified when Phase ${activePhaseTab} status is "NOT STARTED" or "OPEN". Current status: ${phaseStatuses[activePhaseTab]}`);
                                          } else {
                                            handleDeletePhaseSelection(selection._id, selection.selectionType);
                                          }
                                        }}
                                        disabled={!canModifySelections('option', activePhaseTab) || deletingSelection === selection._id}
                                        style={{
                                          backgroundColor: '#EF4444',
                                          color: 'white',
                                          opacity: (!canModifySelections('option', activePhaseTab) || deletingSelection === selection._id) ? 0.7 : 1,
                                          cursor: !canModifySelections('option', activePhaseTab) ? 'not-allowed' : 'pointer'
                                        }}
                                        title={!canModifySelections('option', activePhaseTab) ? `Locked: Phase ${activePhaseTab} status is ${phaseStatuses[activePhaseTab]}` : ''}
                                      >
                                        {deletingSelection === selection._id ? 'Deleting...' : 'Delete'}
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              <tr style={{ fontWeight: '600', borderTop: '2px solid #E5E7EB', backgroundColor: '#F9FAFB' }}>
                                <td className="data-table-primary-cell">Total</td>
                                <td></td>
                                <td></td>
                                <td style={{ textAlign: 'right' }}>
                                  ${phaseSelections
                                    .filter(s => s.selectionType === 'option')
                                    .reduce((sum, s) => sum + (s.cost * (s.quantity || 1)), 0)
                                    .toFixed(2)}
                                </td>
                                <td></td>
                                <td></td>
                                <td style={{ textAlign: 'right' }}>
                                  ${phaseSelections
                                    .filter(s => s.selectionType === 'option')
                                    .reduce((sum, s) => sum + (s.price * (s.quantity || 1)), 0)
                                    .toFixed(2)}
                                </td>
                                <td></td>
                                <td></td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                      </>
                    )}
                  </>
                )}
              </div>
            )}
            {activeTab === 'parking-selection' && (
              <div style={{ gridColumn: '1 / -1', padding: 0 }}>
                {loadingParkingAllocation ? (
                  <div style={{ padding: '40px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <div className="builders-loading-spinner" />
                  </div>
                ) : (
                  <>
                    {/* Status Progress Card */}
                    <div style={{
                      background: 'white',
                      padding: '24px 40px',
                      borderBottom: '1px solid #E5E7EB',
                      overflowX: 'auto',
                      overflowY: 'hidden'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0,
                        position: 'relative',
                        minWidth: 'max-content'
                      }}>
                        {['NOT STARTED', 'OPEN', 'SUBMITTED', 'SELECTION FINAL', 'WAITING FOR PAYMENT', 'COMPLETED', 'CANCELLED - BY CUSTOMER', 'CANCELLED - NO PAYMENT'].map((stage, index) => {
                          const isActive = stage === parkingSelectionStatus;
                          const isFirst = index === 0;
                          const isLast = index === 7;

                          return (
                            <div
                              key={stage}
                              onClick={() => handleParkingStatusClick(stage)}
                              style={{
                                flexShrink: 0,
                                minWidth: '220px',
                                padding: '10px 24px',
                                paddingLeft: isFirst ? '28px' : '24px',
                                paddingRight: isLast ? '28px' : '24px',
                                background: isActive ? '#D4CBC4' : '#48182F',
                                color: isActive ? '#48182F' : 'white',
                                fontSize: '13px',
                                fontWeight: '600',
                                textAlign: 'center',
                                position: 'relative',
                                clipPath: isFirst
                                  ? 'polygon(0 0, calc(100% - 20px) 0, 100% 50%, calc(100% - 20px) 100%, 0 100%)'
                                  : isLast
                                  ? 'polygon(0 0, 100% 0, 100% 100%, 0 100%, 20px 50%)'
                                  : 'polygon(0 0, calc(100% - 20px) 0, 100% 50%, calc(100% - 20px) 100%, 0 100%, 20px 50%)',
                                marginRight: isLast ? '0' : '-20px',
                                transform: isActive ? 'scale(1.05)' : 'scale(1)',
                                zIndex: isActive ? 1 : 0,
                                transition: 'all 0.2s',
                                whiteSpace: 'nowrap',
                                cursor: 'pointer'
                              }}
                            >
                              {stage}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Generate Document Button Bar */}
                    <div style={{
                      background: 'white',
                      padding: '16px 40px',
                      borderBottom: '1px solid #E5E7EB',
                      display: 'flex',
                      justifyContent: 'flex-end',
                      alignItems: 'center'
                    }}>
                      <button
                        className="generate-doc-btn"
                        onClick={() => setShowDocumentTypeModal(true)}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                          <polyline points="14 2 14 8 20 8"></polyline>
                          <line x1="16" y1="13" x2="8" y2="13"></line>
                          <line x1="16" y1="17" x2="8" y2="17"></line>
                          <polyline points="10 9 9 9 8 9"></polyline>
                        </svg>
                        Generate Document
                      </button>
                    </div>

                    {/* Content Card */}
                    <div className="unit-deal-section">
                      {/* Parking Allocated and Selected - Side by Side */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '24px',
                        marginBottom: '32px'
                      }}>
                        {/* Parking Allocated Section */}
                        <div>
                          <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: '#48182F' }}>
                            Parking Allocated
                          </h3>
                          <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                            {parkingAllocated.map((item, index) => (
                              <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontSize: '14px', color: '#6B7280' }}>{item.type}</span>
                                <span style={{ fontSize: '14px', fontWeight: '500' }}>: {item.allocated}</span>
                              </div>
                            ))}
                            {parkingAllocated.length === 0 && (
                              <span style={{ fontSize: '14px', color: '#6B7280' }}>No parking allocated</span>
                            )}
                          </div>
                        </div>

                        {/* Parking Selected Section */}
                        <div>
                          <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: '#48182F' }}>
                            Parking Selected
                          </h3>
                          <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                            {parkingSelected.map((item, index) => (
                              <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontSize: '14px', color: '#6B7280' }}>{item.type}</span>
                                <span style={{ fontSize: '14px', fontWeight: '500' }}>: {item.selected}</span>
                              </div>
                            ))}
                            {parkingSelected.length === 0 && (
                              <span style={{ fontSize: '14px', color: '#6B7280' }}>No parking selected</span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Selected Parking Table */}
                      <div>
                        <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: '#48182F' }}>
                          Selected parking
                        </h3>

                        {selectedParkingSpaces.length === 0 ? (
                          <div style={{
                            textAlign: 'center',
                            padding: '40px',
                            backgroundColor: '#F9FAFB',
                            borderRadius: '8px',
                            color: '#6B7280'
                          }}>
                            No records found
                          </div>
                        ) : (
                          <div className="data-table-container">
                            <table className="data-table" style={{ width: '100%', minWidth: '1200px' }}>
                              <thead>
                                <tr>
                                  <th>Floor</th>
                                  <th>Space No</th>
                                  <th>Type</th>
                                  <th>Cost</th>
                                  <th>Price</th>
                                  <th>Mandatory Add-ons</th>
                                  <th>Mandatory Add-ons Cost</th>
                                  <th>Mandatory Add-ons Price</th>
                                  <th>Add-ons</th>
                                  <th>Add-ons Cost</th>
                                  <th>Add-ons Price</th>
                                  <th>Selected By</th>
                                  <th>Selected At</th>
                                  <th>Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                {selectedParkingSpaces.map((space) => (
                                  <tr key={space._id}>
                                    <td>{space.floor}</td>
                                    <td>{space.spaceNo}</td>
                                    <td>{space.type}</td>
                                    <td>${space.cost.toLocaleString()}</td>
                                    <td>${space.price.toLocaleString()}</td>
                                    <td>
                                      {space.mandatoryAddons.length > 0
                                        ? space.mandatoryAddons.map((addon: any) => addon.name).join(', ')
                                        : '-'}
                                    </td>
                                    <td>${space.mandatoryAddonsCost.toLocaleString()}</td>
                                    <td>${space.mandatoryAddonsPrice.toLocaleString()}</td>
                                    <td>
                                      {space.addons.length > 0
                                        ? space.addons.map((addon: any) => addon.name).join(', ')
                                        : '-'}
                                    </td>
                                    <td>${space.addonsCost.toLocaleString()}</td>
                                    <td>${space.addonsPrice.toLocaleString()}</td>
                                    <td>{space.selectedBy}</td>
                                    <td>
                                      {space.selectedAt
                                        ? new Date(space.selectedAt).toLocaleDateString()
                                        : '-'}
                                    </td>
                                    <td style={{ textAlign: 'center' }}>
                                      <button
                                        disabled={parkingSelectionStatus !== 'NOT STARTED' && parkingSelectionStatus !== 'OPEN'}
                                        style={{
                                          background: 'none',
                                          border: 'none',
                                          color: (parkingSelectionStatus === 'NOT STARTED' || parkingSelectionStatus === 'OPEN') ? '#DC2626' : '#9CA3AF',
                                          cursor: (parkingSelectionStatus === 'NOT STARTED' || parkingSelectionStatus === 'OPEN') ? 'pointer' : 'not-allowed',
                                          padding: '4px',
                                          fontSize: '14px',
                                          opacity: (parkingSelectionStatus === 'NOT STARTED' || parkingSelectionStatus === 'OPEN') ? 1 : 0.5
                                        }}
                                        onClick={() => {
                                          if (parkingSelectionStatus === 'NOT STARTED' || parkingSelectionStatus === 'OPEN') {
                                            setParkingToDelete(space);
                                            setShowDeleteParkingConfirm(true);
                                          }
                                        }}
                                        title={(parkingSelectionStatus === 'NOT STARTED' || parkingSelectionStatus === 'OPEN') ? 'Delete' : 'Cannot delete - status must be NOT STARTED or OPEN'}
                                      >
                                        <Trash2 size={16} />
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
            {activeTab === 'bicycle-parking-selection' && (
              <div style={{ gridColumn: '1 / -1', padding: 0 }}>
                {loadingBicycleAllocation ? (
                  <div style={{ padding: '40px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <div className="builders-loading-spinner" />
                  </div>
                ) : (
                  <>
                    {/* Status Progress Card */}
                    <div style={{
                      background: 'white',
                      padding: '24px 40px',
                      borderBottom: '1px solid #E5E7EB',
                      overflowX: 'auto',
                      overflowY: 'hidden'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0,
                        position: 'relative',
                        minWidth: 'max-content'
                      }}>
                        {['NOT STARTED', 'OPEN', 'SUBMITTED', 'SELECTION FINAL', 'WAITING FOR PAYMENT', 'COMPLETED', 'CANCELLED - BY CUSTOMER', 'CANCELLED - NO PAYMENT'].map((stage, index) => {
                          const isActive = stage === bicycleSelectionStatus;
                          const isFirst = index === 0;
                          const isLast = index === 7;

                          return (
                            <div
                              key={stage}
                              onClick={() => handleBicycleStatusClick(stage)}
                              style={{
                                flexShrink: 0,
                                minWidth: '220px',
                                padding: '10px 24px',
                                paddingLeft: isFirst ? '28px' : '24px',
                                paddingRight: isLast ? '28px' : '24px',
                                background: isActive ? '#D4CBC4' : '#48182F',
                                color: isActive ? '#48182F' : 'white',
                                fontSize: '13px',
                                fontWeight: '600',
                                textAlign: 'center',
                                position: 'relative',
                                clipPath: isFirst
                                  ? 'polygon(0 0, calc(100% - 20px) 0, 100% 50%, calc(100% - 20px) 100%, 0 100%)'
                                  : isLast
                                  ? 'polygon(0 0, 100% 0, 100% 100%, 0 100%, 20px 50%)'
                                  : 'polygon(0 0, calc(100% - 20px) 0, 100% 50%, calc(100% - 20px) 100%, 0 100%, 20px 50%)',
                                marginRight: isLast ? '0' : '-20px',
                                transform: isActive ? 'scale(1.05)' : 'scale(1)',
                                zIndex: isActive ? 1 : 0,
                                transition: 'all 0.2s',
                                whiteSpace: 'nowrap',
                                cursor: 'pointer'
                              }}
                            >
                              {stage}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Generate Document Button Bar */}
                    <div style={{
                      background: 'white',
                      padding: '16px 40px',
                      borderBottom: '1px solid #E5E7EB',
                      display: 'flex',
                      justifyContent: 'flex-end',
                      alignItems: 'center'
                    }}>
                      <button
                        className="generate-doc-btn"
                        onClick={() => setShowDocumentTypeModal(true)}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                          <polyline points="14 2 14 8 20 8"></polyline>
                          <line x1="16" y1="13" x2="8" y2="13"></line>
                          <line x1="16" y1="17" x2="8" y2="17"></line>
                          <polyline points="10 9 9 9 8 9"></polyline>
                        </svg>
                        Generate Document
                      </button>
                    </div>

                    {/* Content Card */}
                    <div className="unit-deal-section">
                      {/* Bicycle Allocated and Selected - Side by Side */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '24px',
                        marginBottom: '32px'
                      }}>
                        {/* Bicycle Allocated Section */}
                        <div>
                          <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: '#48182F' }}>
                            Bicycle Parking Allocated
                          </h3>
                          <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                            {bicycleAllocated.map((item, index) => (
                              <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontSize: '14px', color: '#6B7280' }}>{item.type}</span>
                                <span style={{ fontSize: '14px', fontWeight: '500' }}>: {item.allocated}</span>
                              </div>
                            ))}
                            {bicycleAllocated.length === 0 && (
                              <span style={{ fontSize: '14px', color: '#6B7280' }}>No bicycle parking allocated</span>
                            )}
                          </div>
                        </div>

                        {/* Bicycle Selected Section */}
                        <div>
                          <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: '#48182F' }}>
                            Bicycle Parking Selected
                          </h3>
                          <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                            {bicycleSelected.map((item, index) => (
                              <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontSize: '14px', color: '#6B7280' }}>{item.type}</span>
                                <span style={{ fontSize: '14px', fontWeight: '500' }}>: {item.selected}</span>
                              </div>
                            ))}
                            {bicycleSelected.length === 0 && (
                              <span style={{ fontSize: '14px', color: '#6B7280' }}>No bicycle parking selected</span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Selected Bicycle Spaces Table */}
                      <div>
                        <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: '#48182F' }}>
                          Selected bicycle parking
                        </h3>

                        {selectedBicycleSpaces.length === 0 ? (
                          <div style={{
                            textAlign: 'center',
                            padding: '40px',
                            backgroundColor: '#F9FAFB',
                            borderRadius: '8px',
                            color: '#6B7280'
                          }}>
                            No records found
                          </div>
                        ) : (
                          <div className="data-table-container">
                            <table className="data-table" style={{ width: '100%', minWidth: '1200px' }}>
                              <thead>
                                <tr>
                                  <th>Floor</th>
                                  <th>Space No</th>
                                  <th>Type</th>
                                  <th>Cost</th>
                                  <th>Price</th>
                                  <th>Mandatory Add-ons</th>
                                  <th>Mandatory Add-ons Cost</th>
                                  <th>Mandatory Add-ons Price</th>
                                  <th>Add-ons</th>
                                  <th>Add-ons Cost</th>
                                  <th>Add-ons Price</th>
                                  <th>Selected By</th>
                                  <th>Selected At</th>
                                  <th>Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                {selectedBicycleSpaces.map((space) => (
                                  <tr key={space._id}>
                                    <td>{space.floor}</td>
                                    <td>{space.spaceNo}</td>
                                    <td>{space.type}</td>
                                    <td>${space.cost.toLocaleString()}</td>
                                    <td>${space.price.toLocaleString()}</td>
                                    <td>
                                      {space.mandatoryAddons.length > 0
                                        ? space.mandatoryAddons.map((addon: any) => addon.name).join(', ')
                                        : '-'}
                                    </td>
                                    <td>${space.mandatoryAddonsCost.toLocaleString()}</td>
                                    <td>${space.mandatoryAddonsPrice.toLocaleString()}</td>
                                    <td>
                                      {space.addons.length > 0
                                        ? space.addons.map((addon: any) => addon.name).join(', ')
                                        : '-'}
                                    </td>
                                    <td>${space.addonsCost.toLocaleString()}</td>
                                    <td>${space.addonsPrice.toLocaleString()}</td>
                                    <td>{space.selectedBy}</td>
                                    <td>
                                      {space.selectedAt
                                        ? new Date(space.selectedAt).toLocaleDateString()
                                        : '-'}
                                    </td>
                                    <td style={{ textAlign: 'center' }}>
                                      <button
                                        disabled={bicycleSelectionStatus !== 'NOT STARTED' && bicycleSelectionStatus !== 'OPEN'}
                                        style={{
                                          background: 'none',
                                          border: 'none',
                                          color: (bicycleSelectionStatus === 'NOT STARTED' || bicycleSelectionStatus === 'OPEN') ? '#DC2626' : '#9CA3AF',
                                          cursor: (bicycleSelectionStatus === 'NOT STARTED' || bicycleSelectionStatus === 'OPEN') ? 'pointer' : 'not-allowed',
                                          padding: '4px',
                                          fontSize: '14px',
                                          opacity: (bicycleSelectionStatus === 'NOT STARTED' || bicycleSelectionStatus === 'OPEN') ? 1 : 0.5
                                        }}
                                        onClick={() => {
                                          if (bicycleSelectionStatus === 'NOT STARTED' || bicycleSelectionStatus === 'OPEN') {
                                            setBicycleToDelete(space);
                                            setShowDeleteBicycleConfirm(true);
                                          }
                                        }}
                                        title={(bicycleSelectionStatus === 'NOT STARTED' || bicycleSelectionStatus === 'OPEN') ? 'Delete' : 'Cannot delete - status must be NOT STARTED or OPEN'}
                                      >
                                        <Trash2 size={16} />
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
            {activeTab === 'locker-selection' && (
              <div style={{ gridColumn: '1 / -1', padding: 0 }}>
                {loadingLockerAllocation ? (
                  <div style={{ padding: '40px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <div className="builders-loading-spinner" />
                  </div>
                ) : (
                  <>
                    {/* Status Progress Card */}
                    <div style={{
                      background: 'white',
                      padding: '24px 40px',
                      borderBottom: '1px solid #E5E7EB',
                      overflowX: 'auto',
                      overflowY: 'hidden'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0,
                        position: 'relative',
                        minWidth: 'max-content'
                      }}>
                        {['NOT STARTED', 'OPEN', 'SUBMITTED', 'SELECTION FINAL', 'WAITING FOR PAYMENT', 'COMPLETED', 'CANCELLED - BY CUSTOMER', 'CANCELLED - NO PAYMENT'].map((stage, index) => {
                          const isActive = stage === lockerSelectionStatus;
                          const isFirst = index === 0;
                          const isLast = index === 7;

                          return (
                            <div
                              key={stage}
                              onClick={() => handleLockerStatusClick(stage)}
                              style={{
                                flexShrink: 0,
                                minWidth: '220px',
                                padding: '10px 24px',
                                paddingLeft: isFirst ? '28px' : '24px',
                                paddingRight: isLast ? '28px' : '24px',
                                background: isActive ? '#D4CBC4' : '#48182F',
                                color: isActive ? '#48182F' : 'white',
                                fontSize: '13px',
                                fontWeight: '600',
                                textAlign: 'center',
                                position: 'relative',
                                clipPath: isFirst
                                  ? 'polygon(0 0, calc(100% - 20px) 0, 100% 50%, calc(100% - 20px) 100%, 0 100%)'
                                  : isLast
                                  ? 'polygon(0 0, 100% 0, 100% 100%, 0 100%, 20px 50%)'
                                  : 'polygon(0 0, calc(100% - 20px) 0, 100% 50%, calc(100% - 20px) 100%, 0 100%, 20px 50%)',
                                marginRight: isLast ? '0' : '-20px',
                                transform: isActive ? 'scale(1.05)' : 'scale(1)',
                                zIndex: isActive ? 1 : 0,
                                transition: 'all 0.2s',
                                whiteSpace: 'nowrap',
                                cursor: 'pointer'
                              }}
                            >
                              {stage}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Generate Document Button Bar */}
                    <div style={{
                      background: 'white',
                      padding: '16px 40px',
                      borderBottom: '1px solid #E5E7EB',
                      display: 'flex',
                      justifyContent: 'flex-end',
                      alignItems: 'center'
                    }}>
                      <button
                        className="generate-doc-btn"
                        onClick={() => setShowDocumentTypeModal(true)}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                          <polyline points="14 2 14 8 20 8"></polyline>
                          <line x1="16" y1="13" x2="8" y2="13"></line>
                          <line x1="16" y1="17" x2="8" y2="17"></line>
                          <polyline points="10 9 9 9 8 9"></polyline>
                        </svg>
                        Generate Document
                      </button>
                    </div>

                    {/* Content Card */}
                    <div className="unit-deal-section">
                      {/* Locker Allocated and Selected - Side by Side */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '24px',
                        marginBottom: '32px'
                      }}>
                        {/* Locker Allocated Section */}
                        <div>
                          <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: '#48182F' }}>
                            Locker Allocated
                          </h3>
                          <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                            {lockerAllocated.map((item, index) => (
                              <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontSize: '14px', color: '#6B7280' }}>{item.type}</span>
                                <span style={{ fontSize: '14px', fontWeight: '500' }}>: {item.allocated}</span>
                              </div>
                            ))}
                            {lockerAllocated.length === 0 && (
                              <span style={{ fontSize: '14px', color: '#6B7280' }}>No locker allocated</span>
                            )}
                          </div>
                        </div>

                        {/* Locker Selected Section */}
                        <div>
                          <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: '#48182F' }}>
                            Locker Selected
                          </h3>
                          <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                            {lockerSelected.map((item, index) => (
                              <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontSize: '14px', color: '#6B7280' }}>{item.type}</span>
                                <span style={{ fontSize: '14px', fontWeight: '500' }}>: {item.selected}</span>
                              </div>
                            ))}
                            {lockerSelected.length === 0 && (
                              <span style={{ fontSize: '14px', color: '#6B7280' }}>No locker selected</span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Selected Locker Spaces Table */}
                      <div>
                        <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: '#48182F' }}>
                          Selected locker
                        </h3>

                        {selectedLockerSpaces.length === 0 ? (
                          <div style={{
                            textAlign: 'center',
                            padding: '40px',
                            backgroundColor: '#F9FAFB',
                            borderRadius: '8px',
                            color: '#6B7280'
                          }}>
                            No records found
                          </div>
                        ) : (
                          <div className="data-table-container">
                            <table className="data-table" style={{ width: '100%', minWidth: '1200px' }}>
                              <thead>
                                <tr>
                                  <th>Floor</th>
                                  <th>Space No</th>
                                  <th>Type</th>
                                  <th>Cost</th>
                                  <th>Price</th>
                                  <th>Mandatory Add-ons</th>
                                  <th>Mandatory Add-ons Cost</th>
                                  <th>Mandatory Add-ons Price</th>
                                  <th>Add-ons</th>
                                  <th>Add-ons Cost</th>
                                  <th>Add-ons Price</th>
                                  <th>Selected By</th>
                                  <th>Selected At</th>
                                  <th>Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                {selectedLockerSpaces.map((space) => (
                                  <tr key={space._id}>
                                    <td>{space.floor}</td>
                                    <td>{space.spaceNo}</td>
                                    <td>{space.type}</td>
                                    <td>${space.cost.toLocaleString()}</td>
                                    <td>${space.price.toLocaleString()}</td>
                                    <td>
                                      {space.mandatoryAddons.length > 0
                                        ? space.mandatoryAddons.map((addon: any) => addon.name).join(', ')
                                        : '-'}
                                    </td>
                                    <td>${space.mandatoryAddonsCost.toLocaleString()}</td>
                                    <td>${space.mandatoryAddonsPrice.toLocaleString()}</td>
                                    <td>
                                      {space.addons.length > 0
                                        ? space.addons.map((addon: any) => addon.name).join(', ')
                                        : '-'}
                                    </td>
                                    <td>${space.addonsCost.toLocaleString()}</td>
                                    <td>${space.addonsPrice.toLocaleString()}</td>
                                    <td>{space.selectedBy}</td>
                                    <td>
                                      {space.selectedAt
                                        ? new Date(space.selectedAt).toLocaleDateString()
                                        : '-'}
                                    </td>
                                    <td style={{ textAlign: 'center' }}>
                                      <button
                                        disabled={lockerSelectionStatus !== 'NOT STARTED' && lockerSelectionStatus !== 'OPEN'}
                                        style={{
                                          background: 'none',
                                          border: 'none',
                                          color: (lockerSelectionStatus === 'NOT STARTED' || lockerSelectionStatus === 'OPEN') ? '#DC2626' : '#9CA3AF',
                                          cursor: (lockerSelectionStatus === 'NOT STARTED' || lockerSelectionStatus === 'OPEN') ? 'pointer' : 'not-allowed',
                                          padding: '4px',
                                          fontSize: '14px',
                                          opacity: (lockerSelectionStatus === 'NOT STARTED' || lockerSelectionStatus === 'OPEN') ? 1 : 0.5
                                        }}
                                        onClick={() => {
                                          if (lockerSelectionStatus === 'NOT STARTED' || lockerSelectionStatus === 'OPEN') {
                                            setLockerToDelete(space);
                                            setShowDeleteLockerConfirm(true);
                                          }
                                        }}
                                        title={(lockerSelectionStatus === 'NOT STARTED' || lockerSelectionStatus === 'OPEN') ? 'Delete' : 'Cannot delete - status must be NOT STARTED or OPEN'}
                                      >
                                        <Trash2 size={16} />
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
            {activeTab === 'layout-customizations' && (
              <div style={{ padding: '24px', gridColumn: '1 / -1' }}>
                <h3>Layout Customizations</h3>
                <p>Content coming soon</p>
              </div>
            )}
            {activeTab === 'summary' && (
              <div style={{ padding: '0', gridColumn: '1 / -1' }}>
                {loadingSummary ? (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                    <div className="builders-loading-spinner" />
                  </div>
                ) : summaryData ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '24px' }}>
                    {/* Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#48182F', marginBottom: '4px' }}>
                          Design Studio Summary
                        </h2>
                        <p style={{ fontSize: '13px', color: '#6B7280', margin: 0 }}>
                          Overview of all selections made for this unit
                        </p>
                      </div>
                      <button
                        className="btn btn-primary btn-md"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          whiteSpace: 'nowrap'
                        }}
                        onClick={() => setShowDocumentTypeModal(true)}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                          <polyline points="14 2 14 8 20 8"></polyline>
                          <line x1="16" y1="13" x2="8" y2="13"></line>
                          <line x1="16" y1="17" x2="8" y2="17"></line>
                          <polyline points="10 9 9 9 8 9"></polyline>
                        </svg>
                        Generate Invoice
                      </button>
                    </div>

                    {/* Hero Image */}
                    {summaryData.colorCollection && (
                      <div style={{
                        width: '100%',
                        height: '250px',
                        backgroundColor: '#F3F4F6',
                        border: '1px solid #D1D5DB',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {loadingHeroImage ? (
                          <div className="builders-loading-spinner" style={{ width: '32px', height: '32px' }} />
                        ) : heroImageUrl ? (
                          <img
                            src={heroImageUrl}
                            alt="Color Collection"
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain'
                            }}
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                              (e.target as HTMLImageElement).parentElement!.innerHTML = `
                                <div style="display: flex; flex-direction: column; align-items: center; gap: 8px; color: #9CA3AF;">
                                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                    <circle cx="8.5" cy="8.5" r="1.5"></circle>
                                    <polyline points="21 15 16 10 5 21"></polyline>
                                  </svg>
                                  <span style="font-size: 14px;">No Image Available</span>
                                </div>
                              `;
                            }}
                          />
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', color: '#9CA3AF' }}>
                            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                              <circle cx="8.5" cy="8.5" r="1.5"></circle>
                              <polyline points="21 15 16 10 5 21"></polyline>
                            </svg>
                            <span style={{ fontSize: '14px' }}>No Image Available</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Unit Attributes */}
                    {unitDeal && (
                      <div style={{
                        backgroundColor: '#FFFFFF',
                        border: '1px solid #D1D5DB',
                        borderRadius: '12px',
                        padding: '16px',
                        boxShadow: '0 2px 8px rgba(72, 24, 47, 0.08)'
                      }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                          <div>
                            <div style={{ fontSize: '11px', color: '#6B7280', textTransform: 'uppercase', marginBottom: '4px', fontWeight: '500' }}>
                              PROJECT
                            </div>
                            <div style={{ fontSize: '14px', fontWeight: '600', color: '#48182F' }}>
                              {unitDeal.project}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: '11px', color: '#6B7280', textTransform: 'uppercase', marginBottom: '4px', fontWeight: '500' }}>
                              UNIT NUMBER
                            </div>
                            <div style={{ fontSize: '14px', fontWeight: '600', color: '#48182F' }}>
                              {unitDeal.unitNumber}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: '11px', color: '#6B7280', textTransform: 'uppercase', marginBottom: '4px', fontWeight: '500' }}>
                              MODEL
                            </div>
                            <div style={{ fontSize: '14px', fontWeight: '600', color: '#48182F' }}>
                              {unitDeal.modelName}
                            </div>
                          </div>
                          {unitDeal.type && (
                            <div>
                              <div style={{ fontSize: '11px', color: '#6B7280', textTransform: 'uppercase', marginBottom: '4px', fontWeight: '500' }}>
                                UNIT TYPE
                              </div>
                              <div style={{ fontSize: '14px', fontWeight: '600', color: '#48182F' }}>
                                {unitDeal.type}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Color Collection (Phase One) */}
                    {summaryData.colorCollection && (
                      <div style={{
                        backgroundColor: '#FFFFFF',
                        border: '1px solid #D1D5DB',
                        borderRadius: '12px',
                        boxShadow: '0 2px 8px rgba(72, 24, 47, 0.08)',
                        overflow: 'hidden'
                      }}>
                        {/* Header */}
                        <div style={{
                          backgroundColor: '#48182F',
                          padding: '12px 16px',
                          color: '#FFFFFF'
                        }}>
                          <div style={{ fontSize: '16px', fontWeight: '600' }}>
                            Color Collection
                          </div>
                        </div>

                        {/* Content */}
                        <div style={{ padding: '16px' }}>
                          <div style={{
                            backgroundColor: 'rgba(72, 24, 47, 0.05)',
                            border: '1px solid rgba(72, 24, 47, 0.15)',
                            borderRadius: '8px',
                            padding: '12px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}>
                            <div style={{ fontSize: '13px', fontWeight: '500', color: '#48182F' }}>
                              {summaryData.colorCollection.name}
                            </div>
                            <div style={{ fontSize: '13px', fontWeight: '600', color: '#48182F' }}>
                              ${summaryData.colorCollection.price.toFixed(2)}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Phase Sections */}
                    {summaryData.phaseOrder && summaryData.phaseOrder.map((phaseName: string) => {
                      const phase = summaryData.phases[phaseName];
                      if (!phase || (phase.packages.length === 0 && phase.options.length === 0)) {
                        return null;
                      }

                      const isExpanded = expandedPhases[phaseName];

                      // Group packages and options by location
                      const locationGroups: { [key: string]: { packages: any[], options: any[] } } = {};

                      phase.packages.forEach((pkg: any) => {
                        const location = pkg.location || 'General';
                        if (!locationGroups[location]) {
                          locationGroups[location] = { packages: [], options: [] };
                        }
                        locationGroups[location].packages.push(pkg);
                      });

                      phase.options.forEach((opt: any) => {
                        const location = opt.location || 'General';
                        if (!locationGroups[location]) {
                          locationGroups[location] = { packages: [], options: [] };
                        }
                        locationGroups[location].options.push(opt);
                      });

                      return (
                        <div key={phaseName} style={{
                          backgroundColor: '#FFFFFF',
                          border: '1px solid #D1D5DB',
                          borderRadius: '12px',
                          boxShadow: '0 2px 8px rgba(72, 24, 47, 0.08)',
                          overflow: 'hidden'
                        }}>
                          {/* Phase Header - Clickable */}
                          <div
                            onClick={() => setExpandedPhases({ ...expandedPhases, [phaseName]: !isExpanded })}
                            style={{
                              backgroundColor: '#48182F',
                              padding: '12px 16px',
                              cursor: 'pointer',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}
                          >
                            <div style={{ fontSize: '16px', fontWeight: '600', color: '#FFFFFF' }}>
                              Phase {phaseName}
                            </div>
                            <span style={{
                              color: '#FFFFFF',
                              transition: 'transform 0.2s',
                              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                              display: 'inline-block'
                            }}>
                              ▼
                            </span>
                          </div>

                          {/* Phase Content - Collapsible */}
                          {isExpanded && (
                            <div style={{ padding: '16px' }}>
                              {/* Location Groups */}
                              {Object.entries(locationGroups).map(([location, data], index) => (
                                <div key={location}>
                                  {index > 0 && <div style={{ height: '1px', backgroundColor: '#E5E7EB', margin: '16px 0' }} />}

                                  {/* Location Header */}
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                    <span style={{ color: '#48182F', fontSize: '16px' }}>📍</span>
                                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#48182F' }}>
                                      {location}
                                    </div>
                                  </div>

                                  {/* Packages */}
                                  {data.packages.length > 0 && (
                                    <div style={{ marginBottom: data.options.length > 0 ? '8px' : '0' }}>
                                      <div style={{ fontSize: '11px', fontWeight: '500', color: '#6B7280', marginLeft: '22px', marginBottom: '4px', letterSpacing: '0.3px' }}>
                                        PACKAGES
                                      </div>
                                      {data.packages.map((pkg: any) => (
                                        <div key={pkg.id} style={{ marginLeft: '22px', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                          <div style={{ width: '4px', height: '4px', borderRadius: '50%', backgroundColor: 'rgba(72, 24, 47, 0.6)' }} />
                                          <div style={{ flex: 1, fontSize: '12px', color: '#374151' }}>
                                            {pkg.name}
                                            {pkg.includedOptions && pkg.includedOptions.length > 0 && (
                                              <span style={{ fontSize: '11px', color: '#6B7280', marginLeft: '4px' }}>
                                                (includes: {pkg.includedOptions.map((opt: any) => opt.name).join(', ')})
                                              </span>
                                            )}
                                          </div>
                                          <div style={{ fontSize: '12px', fontWeight: '500', color: '#111827' }}>
                                            ${pkg.price.toFixed(2)}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  {/* Options */}
                                  {data.options.length > 0 && (
                                    <div>
                                      <div style={{ fontSize: '11px', fontWeight: '500', color: '#6B7280', marginLeft: '22px', marginBottom: '4px', letterSpacing: '0.3px' }}>
                                        OPTIONS
                                      </div>
                                      {data.options.map((opt: any) => (
                                        <div key={opt.id} style={{ marginLeft: '22px', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                          <div style={{ width: '4px', height: '4px', borderRadius: '50%', backgroundColor: '#9CA3AF' }} />
                                          <div style={{ flex: 1, fontSize: '12px', color: '#374151' }}>
                                            {opt.name}
                                            <span style={{ fontSize: '11px', color: '#6B7280', marginLeft: '4px' }}>
                                              (Quantity: {opt.quantity || 1})
                                            </span>
                                          </div>
                                          <div style={{ fontSize: '12px', fontWeight: '500', color: '#111827' }}>
                                            ${((opt.price || 0) * (opt.quantity || 1)).toFixed(2)}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ))}

                              {/* Phase Totals */}
                              <div style={{ height: '1px', backgroundColor: '#E5E7EB', margin: '16px 0' }} />
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                                  <div style={{ color: '#6B7280' }}>Subtotal:</div>
                                  <div style={{ fontWeight: '600', color: '#48182F' }}>${phase.subtotal.toFixed(2)}</div>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                                  <div style={{ color: '#6B7280' }}>HST (13%):</div>
                                  <div style={{ fontWeight: '600', color: '#48182F' }}>${phase.hst.toFixed(2)}</div>
                                </div>
                                <div style={{ height: '1px', backgroundColor: '#E5E7EB', margin: '4px 0' }} />
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                                  <div style={{ fontWeight: '600', color: '#48182F' }}>Phase Total:</div>
                                  <div style={{ fontWeight: 'bold', color: '#48182F' }}>${phase.total.toFixed(2)}</div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Grand Totals */}
                    <div style={{
                      backgroundColor: '#FFFFFF',
                      border: '2px solid #48182F',
                      borderRadius: '12px',
                      padding: '20px',
                      boxShadow: '0 4px 12px rgba(72, 24, 47, 0.15)'
                    }}>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: '#48182F', marginBottom: '16px' }}>
                        Total Summary
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                          <div style={{ color: '#6B7280' }}>Subtotal:</div>
                          <div style={{ fontWeight: '600', color: '#48182F' }}>${summaryData.grandSubtotal.toFixed(2)}</div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                          <div style={{ color: '#6B7280' }}>HST (13%):</div>
                          <div style={{ fontWeight: '600', color: '#48182F' }}>${summaryData.grandHst.toFixed(2)}</div>
                        </div>
                        {summaryData.decorDollar > 0 && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                            <div style={{ color: '#10B981' }}>Décor Dollar:</div>
                            <div style={{ fontWeight: '600', color: '#10B981' }}>-${summaryData.decorDollar.toFixed(2)}</div>
                          </div>
                        )}
                        <div style={{ height: '2px', backgroundColor: '#48182F', margin: '8px 0' }} />
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '18px' }}>
                          <div style={{ fontWeight: 'bold', color: '#48182F' }}>Grand Total:</div>
                          <div style={{ fontWeight: 'bold', color: '#48182F' }}>${summaryData.grandTotal.toFixed(2)}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                    No summary data available
                  </div>
                )}
              </div>
            )}
            {activeTab === 'documents' && (
              <div style={{ padding: '24px', gridColumn: '1 / -1' }}>
                {unitDeal?.dealId ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                    {/* Décor Studio Amendments */}
                    <div className="unit-deal-document-card">
                      <DocumentGallery
                        workspaceId={workspaceId}
                        entityType="DEAL_DOCUMENTS"
                        entityId={unitDeal.dealId}
                        fileType="decor_studio_amendment"
                        acceptTypes=".pdf,.doc,.docx"
                        title="Décor Studio Amendments"
                      />
                    </div>

                    {/* Preferred Parking Amendments */}
                    <div className="unit-deal-document-card">
                      <DocumentGallery
                        workspaceId={workspaceId}
                        entityType="DEAL_DOCUMENTS"
                        entityId={unitDeal.dealId}
                        fileType="preferred_parking_amendment"
                        acceptTypes=".pdf,.doc,.docx"
                        title="Preferred Parking Amendments"
                      />
                    </div>

                    {/* PDI */}
                    <div className="unit-deal-document-card">
                      <DocumentGallery
                        workspaceId={workspaceId}
                        entityType="DEAL_DOCUMENTS"
                        entityId={unitDeal.dealId}
                        fileType="pdi"
                        acceptTypes=".pdf,.doc,.docx"
                        title="PDI"
                      />
                    </div>

                    {/* CCP */}
                    <div className="unit-deal-document-card">
                      <DocumentGallery
                        workspaceId={workspaceId}
                        entityType="DEAL_DOCUMENTS"
                        entityId={unitDeal.dealId}
                        fileType="ccp"
                        acceptTypes=".pdf,.doc,.docx"
                        title="CCP"
                      />
                    </div>

                    {/* FAQ */}
                    <div className="unit-deal-document-card">
                      <DocumentGallery
                        workspaceId={workspaceId}
                        entityType="DEAL_DOCUMENTS"
                        entityId={unitDeal.dealId}
                        fileType="faq"
                        acceptTypes=".pdf,.doc,.docx"
                        title="FAQ"
                      />
                    </div>

                    {/* Condo Documents */}
                    <div className="unit-deal-document-card">
                      <DocumentGallery
                        workspaceId={workspaceId}
                        entityType="DEAL_DOCUMENTS"
                        entityId={unitDeal.dealId}
                        fileType="condo_document"
                        acceptTypes=".pdf,.doc,.docx"
                        title="Condo Documents"
                      />
                    </div>

                    {/* Other Documents */}
                    <div className="unit-deal-document-card">
                      <DocumentGallery
                        workspaceId={workspaceId}
                        entityType="DEAL_DOCUMENTS"
                        entityId={unitDeal.dealId}
                        fileType="other_document"
                        acceptTypes=".pdf,.doc,.docx"
                        title="Other Documents"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="unit-deal-empty-state">
                    <p>No deal associated with this unit. Documents cannot be uploaded.</p>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10001
          }}
          onClick={handleCancelChange}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, marginBottom: '16px', fontSize: '18px', fontWeight: '600' }}>
              Confirmation
            </h3>
            <p style={{ marginBottom: '24px', color: '#6B7280', lineHeight: '1.5' }}>
              Changing color collection will reset the selection made on the Package and Personalization Option. Do you want to continue?
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={handleCancelChange}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#E5E7EB',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleConfirmChange}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#48182F',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Information Modal */}
      {showInfoModal && infoModalData && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10001
          }}
          onClick={() => setShowInfoModal(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '0',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '80vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid #E5E7EB',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
                Information
              </h3>
              <button
                onClick={() => setShowInfoModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6B7280',
                  padding: '0',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>

            {/* Content */}
            <div style={{
              padding: '24px',
              overflowY: 'auto',
              flex: 1
            }}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  Description :
                </label>
                <div style={{
                  color: '#6B7280',
                  lineHeight: '1.6',
                  whiteSpace: 'pre-wrap'
                }}>
                  {stripHtmlTags(infoModalData.description) || '-'}
                </div>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  Construction Notes :
                </label>
                <div style={{
                  color: '#6B7280',
                  lineHeight: '1.6',
                  whiteSpace: 'pre-wrap'
                }}>
                  {stripHtmlTags(infoModalData.constructionNotes) || '-'}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div style={{
              padding: '16px 24px',
              borderTop: '1px solid #E5E7EB',
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setShowInfoModal(false)}
                style={{
                  padding: '8px 20px',
                  backgroundColor: '#E5E7EB',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Change Confirmation Dialog */}
      {showStatusConfirmDialog && pendingStatusChange && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            maxWidth: '500px',
            width: '100%'
          }}>
            {/* Header */}
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid #E5E7EB'
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '18px',
                fontWeight: '600',
                color: '#111827'
              }}>
                Confirm Status Change
              </h3>
            </div>

            {/* Body */}
            <div style={{
              padding: '24px',
              fontSize: '14px',
              color: '#374151',
              lineHeight: '1.6'
            }}>
              Do you want to change the status to <strong>{pendingStatusChange.status}</strong>?
            </div>

            {/* Footer */}
            <div style={{
              padding: '16px 24px',
              borderTop: '1px solid #E5E7EB',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              <button
                onClick={handleCancelStatusChange}
                disabled={updatingStatus}
                style={{
                  padding: '8px 20px',
                  backgroundColor: '#E5E7EB',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: updatingStatus ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: updatingStatus ? 0.5 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmStatusChange}
                disabled={updatingStatus}
                style={{
                  padding: '8px 20px',
                  backgroundColor: '#48182F',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: updatingStatus ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: updatingStatus ? 0.5 : 1
                }}
              >
                {updatingStatus ? 'Updating...' : 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Parking Status Change Confirmation Dialog */}
      {showParkingStatusConfirmDialog && pendingParkingStatusChange && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            maxWidth: '500px',
            width: '100%'
          }}>
            {/* Header */}
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid #E5E7EB'
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '18px',
                fontWeight: '600',
                color: '#111827'
              }}>
                Confirm Status Change
              </h3>
            </div>

            {/* Body */}
            <div style={{
              padding: '24px',
              fontSize: '14px',
              color: '#374151',
              lineHeight: '1.6'
            }}>
              Do you want to change the parking selection status to <strong>{pendingParkingStatusChange}</strong>?
            </div>

            {/* Footer */}
            <div style={{
              padding: '16px 24px',
              borderTop: '1px solid #E5E7EB',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              <button
                onClick={handleCancelParkingStatusChange}
                disabled={updatingParkingStatus}
                style={{
                  padding: '8px 20px',
                  backgroundColor: '#E5E7EB',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: updatingParkingStatus ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: updatingParkingStatus ? 0.5 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmParkingStatusChange}
                disabled={updatingParkingStatus}
                style={{
                  padding: '8px 20px',
                  backgroundColor: '#48182F',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: updatingParkingStatus ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: updatingParkingStatus ? 0.5 : 1
                }}
              >
                {updatingParkingStatus ? 'Updating...' : 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bicycle Status Change Confirmation Dialog */}
      {showBicycleStatusConfirmDialog && pendingBicycleStatusChange && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            maxWidth: '500px',
            width: '100%'
          }}>
            {/* Header */}
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid #E5E7EB'
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '18px',
                fontWeight: '600',
                color: '#111827'
              }}>
                Confirm Status Change
              </h3>
            </div>

            {/* Body */}
            <div style={{
              padding: '24px',
              fontSize: '14px',
              color: '#374151',
              lineHeight: '1.6'
            }}>
              Do you want to change the bicycle selection status to <strong>{pendingBicycleStatusChange}</strong>?
            </div>

            {/* Footer */}
            <div style={{
              padding: '16px 24px',
              borderTop: '1px solid #E5E7EB',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              <button
                onClick={handleCancelBicycleStatusChange}
                disabled={updatingBicycleStatus}
                style={{
                  padding: '8px 20px',
                  backgroundColor: '#E5E7EB',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: updatingBicycleStatus ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: updatingBicycleStatus ? 0.5 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmBicycleStatusChange}
                disabled={updatingBicycleStatus}
                style={{
                  padding: '8px 20px',
                  backgroundColor: '#48182F',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: updatingBicycleStatus ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: updatingBicycleStatus ? 0.5 : 1
                }}
              >
                {updatingBicycleStatus ? 'Updating...' : 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Locker Status Change Confirmation Dialog */}
      {showLockerStatusConfirmDialog && pendingLockerStatusChange && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            maxWidth: '500px',
            width: '100%'
          }}>
            {/* Header */}
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid #E5E7EB'
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '18px',
                fontWeight: '600',
                color: '#111827'
              }}>
                Confirm Status Change
              </h3>
            </div>

            {/* Body */}
            <div style={{
              padding: '24px',
              fontSize: '14px',
              color: '#374151',
              lineHeight: '1.6'
            }}>
              Do you want to change the locker selection status to <strong>{pendingLockerStatusChange}</strong>?
            </div>

            {/* Footer */}
            <div style={{
              padding: '16px 24px',
              borderTop: '1px solid #E5E7EB',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              <button
                onClick={handleCancelLockerStatusChange}
                disabled={updatingLockerStatus}
                style={{
                  padding: '8px 20px',
                  backgroundColor: '#E5E7EB',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: updatingLockerStatus ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: updatingLockerStatus ? 0.5 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmLockerStatusChange}
                disabled={updatingLockerStatus}
                style={{
                  padding: '8px 20px',
                  backgroundColor: '#48182F',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: updatingLockerStatus ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: updatingLockerStatus ? 0.5 : 1
                }}
              >
                {updatingLockerStatus ? 'Updating...' : 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Parking Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteParkingConfirm}
        onClose={handleCancelDeleteParking}
        onConfirm={handleConfirmDeleteParking}
        title="Remove Parking Space"
        message={`Are you sure you want to remove parking space ${parkingToDelete?.spaceNo || ''} from this unit?`}
        confirmText="Remove"
        cancelText="Cancel"
        confirmButtonStyle="danger"
        loading={isDeletingParking}
      />

      {/* Delete Bicycle Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteBicycleConfirm}
        onClose={handleCancelDeleteBicycle}
        onConfirm={handleConfirmDeleteBicycle}
        title="Remove Bicycle Space"
        message={`Are you sure you want to remove bicycle space ${bicycleToDelete?.spaceNo || ''} from this unit?`}
        confirmText="Remove"
        cancelText="Cancel"
        confirmButtonStyle="danger"
        loading={isDeletingBicycle}
      />

      {/* Delete Locker Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteLockerConfirm}
        onClose={handleCancelDeleteLocker}
        onConfirm={handleConfirmDeleteLocker}
        title="Remove Locker"
        message={`Are you sure you want to remove locker ${lockerToDelete?.spaceNo || ''} from this unit?`}
        confirmText="Remove"
        cancelText="Cancel"
        confirmButtonStyle="danger"
        loading={isDeletingLocker}
      />

      {/* Document Type Selection Modal */}
      {showDocumentTypeModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>Select Document Type</h2>
              <button
                className="modal-close-btn"
                onClick={() => setShowDocumentTypeModal(false)}
              >
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '20px' }}>
                Choose the type of document you want to generate:
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {getAvailableDocumentTypes().map((docType) => (
                  <div
                    key={docType.type}
                    onClick={() => {
                      setShowDocumentTypeModal(false);
                      handleOpenTemplateSelection(docType.type);
                    }}
                    style={{
                      padding: '16px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      background: '#FFFFFF',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#48182F';
                      e.currentTarget.style.background = 'rgba(72, 24, 47, 0.02)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#D1D5DB';
                      e.currentTarget.style.background = '#FFFFFF';
                    }}
                  >
                    <h4 style={{
                      fontSize: '15px',
                      fontWeight: '600',
                      color: '#48182F',
                      margin: '0 0 4px 0'
                    }}>
                      {docType.label}
                    </h4>
                    <p style={{
                      fontSize: '13px',
                      color: '#6B7280',
                      margin: 0
                    }}>
                      {docType.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-secondary btn-md"
                onClick={() => setShowDocumentTypeModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Template Selection Modal */}
      {showTemplateSelectModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Select Template - {getTemplateTypeLabel(selectedGenerationType)}</h2>
              <button
                className="modal-close-btn"
                onClick={() => setShowTemplateSelectModal(false)}
              >
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              {loadingTemplates ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                  <div className="builders-loading-spinner" />
                </div>
              ) : availableTemplates.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '40px 20px',
                  background: '#F9FAFB',
                  borderRadius: '8px',
                  border: '1px dashed #D1D5DB'
                }}>
                  <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '12px' }}>
                    No templates available for this document type.
                  </p>
                  <p style={{ fontSize: '13px', color: '#9CA3AF' }}>
                    Please create a template in Project Settings → Templates tab.
                  </p>
                </div>
              ) : (
                <div>
                  <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '16px' }}>
                    Select a template to generate the document:
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {availableTemplates.map((template) => (
                      <div
                        key={template._id}
                        onClick={() => setSelectedTemplateId(template._id)}
                        style={{
                          padding: '16px',
                          border: `2px solid ${selectedTemplateId === template._id ? '#48182F' : '#D1D5DB'}`,
                          borderRadius: '8px',
                          cursor: 'pointer',
                          background: selectedTemplateId === template._id ? 'rgba(72, 24, 47, 0.05)' : '#FFFFFF',
                          transition: 'all 0.2s'
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}>
                          <div>
                            <h4 style={{
                              fontSize: '15px',
                              fontWeight: '600',
                              color: '#48182F',
                              margin: '0 0 4px 0'
                            }}>
                              {template.name}
                            </h4>
                            {template.description && (
                              <p style={{
                                fontSize: '13px',
                                color: '#6B7280',
                                margin: 0
                              }}>
                                {template.description}
                              </p>
                            )}
                            <p style={{
                              fontSize: '12px',
                              color: '#9CA3AF',
                              margin: '4px 0 0 0'
                            }}>
                              File: {template.fileName}
                            </p>
                          </div>
                          {selectedTemplateId === template._id && (
                            <div style={{
                              width: '20px',
                              height: '20px',
                              borderRadius: '50%',
                              background: '#48182F',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <span style={{ color: 'white', fontSize: '14px' }}>✓</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-secondary btn-md"
                onClick={() => setShowTemplateSelectModal(false)}
                disabled={generatingDocument}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary btn-md"
                onClick={handleGenerateFromTemplate}
                disabled={!selectedTemplateId || generatingDocument || availableTemplates.length === 0}
              >
                {generatingDocument ? 'Generating...' : 'Generate Document'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnitDealDetailsSlider;
